/**
 * All UI copy (Bahasa Indonesia). Components never hardcode user-facing strings.
 * Tone: friendly, plain, benefit-driven. Use "Anda".
 */
/** "Profesional" → "Paket Profesional": upgrade copy always names the package in full. */
const paket = (name: string) => (/^paket\b/i.test(name.trim()) ? name.trim() : `Paket ${name.trim()}`);

export const t = {
  brand: {
    product: "BazeBuild",
    by: "Bagian dari Baze Technology",
    tagline: "Website bisnis profesional dalam 5 menit.",
  },
  common: {
    loading: "Memuat…",
    save: "Simpan",
    saving: "Menyimpan…",
    saved: "Tersimpan",
    cancel: "Batal",
    close: "Tutup",
    back: "Kembali",
    next: "Lanjut",
    continue: "Lanjutkan",
    retry: "Coba lagi",
    edit: "Edit",
    view: "Lihat",
    delete: "Hapus",
    remove: "Hapus",
    copy: "Salin",
    copied: "Tersalin",
    copyLink: "Salin tautan",
    add: "Tambah",
    apply: "Terapkan",
    reset: "Atur ulang",
    confirm: "Konfirmasi",
    yes: "Ya",
    no: "Tidak",
    optional: "opsional",
    required: "wajib",
    more: "Lainnya",
    search: "Cari",
    showMore: "Lihat selengkapnya",
    showLess: "Sembunyikan",
    free: "Gratis",
    premium: "Premium",
    recommended: "Direkomendasikan",
    new: "Baru",
    or: "atau",
    perYear: "/tahun",
    years: (n: number) => `${n} tahun`,
    chars: (n: number, max: number) => `${n}/${max}`,
    moreActions: "Aksi lainnya",
    openMenu: "Buka menu",
    undo: "Urungkan",
    done: "Selesai",
    learnMore: "Pelajari",
    total: "Total",
    status: "Status",
    none: "—",
    whatsapp: "WhatsApp",
    help: "Bantuan",
    helpWhatsappMessage: "Halo tim Baze, saya butuh bantuan dengan BazeBuild.",
    typeToConfirm: (word: string) => `Ketik ${word} untuk mengonfirmasi`,
    confirmWord: "HAPUS",
    skipToContent: "Lewati ke konten utama",
    offlineBanner: "Anda sedang offline. Perubahan akan dikirim saat koneksi kembali.",
  },
  errors: {
    generic: "Terjadi kesalahan. Silakan coba lagi.",
    loadFailed: "Gagal memuat data",
    loadFailedBody: "Periksa koneksi internet Anda, lalu coba lagi. Data Anda aman.",
    notFoundTitle: "Halaman tidak ditemukan",
    notFoundBody: "Tautan ini mungkin salah ketik, atau halamannya sudah dipindahkan.",
    notFoundCta: "Ke Website saya",
    siteNotFound: "Website ini tidak ditemukan atau Anda tidak punya akses.",
    crashTitle: "Ups, ada yang tidak beres",
    crashBody: "Halaman ini mengalami gangguan. Muat ulang biasanya menyelesaikannya.",
    reload: "Muat ulang",
    offline: "Koneksi terputus. Periksa internet Anda lalu coba lagi.",
    rateLimited: (s?: number) =>
      s ? `Terlalu sering. Coba lagi dalam ${s} detik.` : "Terlalu sering. Coba lagi sebentar lagi.",
    requestId: (id: string) => `Kode: ${id}`,
  },
  validation: {
    required: "Wajib diisi.",
    email: "Format email belum benar.",
    passwordMin: "Minimal 8 karakter.",
    passwordMax: "Maksimal 72 karakter.",
    passwordMismatch: "Kata sandi tidak sama.",
    max: (n: number) => `Maksimal ${n} karakter.`,
    min: (n: number) => `Minimal ${n} karakter.`,
    whatsapp: "Nomor WhatsApp belum valid. Contoh: 0812-3456-7890.",
    phone: "Nomor telepon belum valid.",
    url: "Tautan harus diawali https://",
    href: "Tautan belum valid. Gunakan https://…, #bagian, atau /halaman.",
    terms: "Setujui syarat & ketentuan untuk melanjutkan.",
    postalCode: "Kode pos 5 angka.",
    hostname: "Nama domain belum valid. Contoh: tokosaya.com",
    slug: "Hanya huruf kecil, angka, dan tanda hubung (3–40 karakter).",
    hex: "Format warna #RRGGBB.",
    time: "Format jam HH:MM.",
    handle: "Isi nama akun saja, tanpa @ atau tautan.",
  },
  auth: {
    loginTitle: "Masuk ke BazeBuild",
    loginSubtitle: "Kelola website bisnis Anda di satu tempat.",
    registerTitle: "Buat akun gratis",
    registerSubtitle: "Gratis selamanya untuk 1 website dengan subdomain.",
    name: "Nama lengkap",
    namePlaceholder: "Nama Anda",
    email: "Email",
    emailPlaceholder: "nama@email.com",
    password: "Kata sandi",
    passwordPlaceholder: "Minimal 8 karakter",
    whatsapp: "Nomor WhatsApp",
    whatsappHint: "Agar tim kami bisa bantu setup website Anda.",
    whatsappInvalid: "Nomor WhatsApp tidak valid. Contoh: 0812-3456-7890.",
    newPassword: "Kata sandi baru",
    confirmPassword: "Ulangi kata sandi",
    showPassword: "Tampilkan kata sandi",
    hidePassword: "Sembunyikan kata sandi",
    forgot: "Lupa kata sandi?",
    login: "Masuk",
    loggingIn: "Masuk…",
    register: "Daftar",
    registering: "Membuat akun…",
    noAccount: "Belum punya akun?",
    haveAccount: "Sudah punya akun?",
    registerLink: "Daftar gratis",
    loginLink: "Masuk",
    google: "Lanjutkan dengan Google",
    orEmail: "atau dengan email",
    terms: "Saya setuju dengan",
    termsLink: "Syarat & Ketentuan",
    and: "dan",
    privacyLink: "Kebijakan Privasi",
    strength: ["Terlalu pendek", "Lemah", "Cukup", "Kuat", "Sangat kuat"] as const,
    strengthLabel: "Kekuatan kata sandi",
    strengthHint: "Gunakan campuran huruf besar, angka, dan simbol.",
    capsLock: "Caps Lock aktif",
    forgotTitle: "Lupa kata sandi",
    forgotSubtitle: "Masukkan email akun Anda. Kami kirimkan tautan untuk membuat kata sandi baru.",
    forgotSubmit: "Kirim tautan",
    forgotSent: "Cek email Anda",
    forgotSentBody: (email: string) =>
      `Jika ${email} terdaftar, tautan atur ulang kata sandi sudah kami kirim. Tautan berlaku 1 jam.`,
    backToLogin: "Kembali ke halaman masuk",
    resetTitle: "Buat kata sandi baru",
    resetSubtitle: "Kata sandi baru akan mengeluarkan akun Anda dari perangkat lain.",
    resetSubmit: "Simpan kata sandi",
    resetDone: "Kata sandi diperbarui. Silakan masuk.",
    resetInvalid: "Tautan atur ulang tidak valid atau sudah kedaluwarsa.",
    verifyTitle: "Verifikasi email",
    verifying: "Memverifikasi email Anda…",
    verified: "Email terverifikasi. Terima kasih!",
    verifyFailed: "Tautan verifikasi tidak valid atau sudah kedaluwarsa.",
    verifyResend: "Kirim ulang email verifikasi",
    verifyResent: "Email verifikasi dikirim ulang.",
    verifyBanner: (email: string) =>
      `Cek inbox ${email} dan klik link verifikasi. Website baru bisa dipublikasikan setelah email terverifikasi.`,
    continueToApp: "Lanjut ke Website saya",
    callbackTitle: "Menyelesaikan masuk…",
    callbackFailed: "Masuk dengan Google gagal. Silakan coba lagi.",
    sessionExpired: "Sesi Anda berakhir. Silakan masuk lagi.",
    welcome: (name: string) => `Selamat datang, ${name.split(" ")[0]}!`,
    welcomeBack: (name: string) => `Hai lagi, ${name.split(" ")[0]}!`,
    loggedOut: "Anda sudah keluar.",
    signupDisabled: "Pendaftaran sedang ditutup sementara.",
    sideTitle: "Website bisnis profesional dalam 5 menit.",
    sidePoints: [
      "Contoh isi sesuai jenis usaha Anda",
      "Tombol WhatsApp di setiap halaman",
      "Domain sendiri + SSL otomatis",
    ] as const,
  },
  shell: {
    mySites: "Website saya",
    orders: "Tagihan",
    account: "Akun",
    admin: "Admin",
    logout: "Keluar",
    siteSwitcher: "Ganti website",
    allSites: "Semua website",
    newSite: "Buat website baru",
    announcementDismiss: "Tutup pengumuman",
    help: "Bantuan",
    helpTooltip: "Chat tim Baze via WhatsApp",
    mainNav: "Navigasi utama",
    userMenu: "Menu akun",
  },
  splash: { loading: "Menyiapkan BazeBuild…" },
  wizard: {
    title: "Buat website",
    steps: ["Template", "Info dasar", "Tema", "Buat website"] as const,
    stepOf: (n: number, total: number) => `Langkah ${n} dari ${total}`,
    exit: "Keluar dari pembuatan website",
    savedLocally: "Isian Anda tersimpan di perangkat ini.",
    template: {
      title: "Pilih jenis usaha & template",
      subtitle: "Semuanya bisa diganti nanti.",
      search: "Cari: kopi, salon, undangan…",
      categories: "Kategori usaha",
      allCategories: "Semua kategori",
      allInGroup: (group: string) => `Semua ${group}`,
      price: "Harga",
      priceAll: "Semua",
      priceFree: "Gratis",
      pricePremium: "Premium",
      count: (n: number) => `${n} template`,
      resultsFor: (q: string) => `Hasil untuk “${q}”`,
      matchedCategories: "Kategori yang cocok:",
      empty: "Tidak ada template yang cocok.",
      emptyHint: "Coba kata lain, hapus filter, atau lihat semua kategori.",
      clearFilters: "Hapus filter",
      showMore: (n: number) => `Tampilkan ${n} template lagi`,
      preview: "Pratinjau",
      previewOf: (name: string) => `Pratinjau ${name}`,
      previewPage: "Halaman",
      previewHint: "Isi dan tema bisa diubah nanti.",
      use: "Pakai template ini",
      using: "Template dipilih",
      prev: "Template sebelumnya",
      next: "Template berikutnya",
      picked: (name: string, industry: string) => `${name} · ${industry}`,
      pickedLabel: "Dipilih:",
      highlight: "Sorotan",
    },
    info: {
      title: "Ceritakan sedikit tentang usaha Anda",
      subtitle: "Cukup yang Anda tahu — semuanya bisa diubah nanti.",
      aiNote: "AI bantu buatkan isi website, Anda bebas mengubahnya kapan saja. Kendali 100% di tangan Anda.",
      note: "Kami siapkan contoh isi sesuai jenis usaha Anda, dan Anda bebas mengubahnya kapan saja. Kendali 100% di tangan Anda.",
      name: "Nama usaha",
      namePlaceholder: "mis. Kopi Senja",
      logo: "Logo",
      logoHint: "PNG, JPG, atau WebP. Maksimal 10 MB. Logo muncul di menu website.",
      logoEmpty: "Belum ada logo",
      logoSaving: "Menambahkan logo…",
      logoFailed: "Logo belum terunggah. Anda bisa menambahkannya lagi di editor.",
      city: "Kota",
      cityPlaceholder: "mis. Bandung",
      whatsapp: "Nomor WhatsApp",
      whatsappHint: "Pelanggan menghubungi Anda lewat nomor ini.",
      whatsappPlaceholder: "0812-3456-7890",
      testWhatsapp: "Tes link WhatsApp",
      description: "Deskripsi singkat",
      descriptionPlaceholder: "Apa yang membuat usaha Anda istimewa?",
      descriptionExamples: "Contoh — ketuk untuk memakai:",
      offerings: (label: string) => label || "Produk / layanan utama",
      offeringsPlaceholder: "Ketik lalu tekan Enter",
      offeringsHint: (max: number) => `Tekan Enter untuk menambah. Maksimal ${max}.`,
      offeringPrice: "Harga (opsional)",
      offeringAdd: "Tambah",
      offeringRemove: (name: string) => `Hapus ${name}`,
      offeringLimit: (max: number) => `Maksimal ${max} item.`,
      audience: "Target pelanggan",
      audiencePlaceholder: "mis. mahasiswa, pekerja kantoran",
      style: "Gaya bahasa",
      styles: {
        santai: "Santai",
        profesional: "Profesional",
        hangat: "Hangat",
        mewah: "Mewah",
        ceria: "Ceria",
      },
      examples: "Contoh:",
    },
    look: {
      title: "Pilih tema website",
      subtitle: "Lihat langsung dengan nama usaha Anda. Tema bisa diganti kapan saja di editor.",
      templateLabel: "Template:",
      changeTemplate: "Ganti template",
      themeTitle: "Tema",
      themeSubtitle: "Warna, huruf, dan gaya visual. Kami sudah pilihkan yang cocok; ganti kalau Anda mau.",
      themePicked: (name: string) => `Tema dipilih: ${name}`,
      showAllThemes: (n: number) => `Lihat semua tema (${n})`,
      hideThemes: "Tampilkan lebih sedikit",
      premiumNote:
        "Premium bisa dicoba dan diedit gratis. Bayar sekali saat publish, atau pakai voucher promo di bawah.",
      selected: "Dipilih",
      choose: "Pilih",
      noThemes: "Belum ada tema untuk dipilih.",
    },
    create: {
      title: "Siap membuat website Anda",
      subtitle: "Buat akun gratis untuk menyimpan website — isian Anda tidak akan hilang.",
      summary: "Ringkasan",
      summaryIndustry: "Jenis usaha",
      summaryName: "Nama",
      summaryCity: "Kota",
      summaryLogo: "Logo",
      summaryTemplate: "Template",
      summaryTheme: "Tema",
      editStep: "Ubah",
      submit: "Buat website saya",
      submitting: "Membuat…",
      accountTitle: "Simpan website ke akun Anda",
      toAccount: "Daftar & simpan",
      tabRegister: "Daftar",
      tabLogin: "Sudah punya akun",
      loggedInAs: (email: string) => `Masuk sebagai ${email}`,
    },
    progress: {
      title: "Sedang membuat website Anda",
      subtitle: "Biasanya 1–2 menit. Jangan tutup halaman ini.",
      subtitleQuick: "Hanya beberapa detik.",
      beats: [
        "Membaca info usaha",
        "Menyusun struktur",
        "Menulis konten",
        "Menerapkan tema",
        "Menyiapkan pratinjau",
      ] as const,
      queued: (n: number) => `Antrian ke-${n}`,
      queuedHint: "AI sedang melayani pengguna lain. Posisi Anda akan maju otomatis.",
      eta: (seconds: number) =>
        seconds < 60
          ? "Perkiraan selesai kurang dari 1 menit lagi"
          : `Perkiraan selesai ±${Math.round(seconds / 60)} menit lagi`,
      fallback: "AI sedang sibuk, kami pakai konten awal. Anda bisa minta AI menulis ulang per bagian.",
      skip: "Lewati, pakai konten awal",
      skipHint: "Website langsung jadi dengan teks contoh; AI bisa menulis ulang per bagian di editor.",
      skipped: "Konten awal dipakai. Anda bisa minta AI menulis ulang per bagian di editor.",
      done: "Website Anda siap!",
      openEditor: "Buka editor",
      failed: "Pembuatan gagal, tapi website Anda sudah tersimpan dengan konten awal.",
    },
    errors: {
      pickTemplate: "Pilih template dulu.",
      pickTheme: "Pilih tema dulu.",
      catalog: "Katalog tema belum bisa dimuat.",
    },
  },
  sites: {
    title: "Website saya",
    subtitle: "Semua website Anda di satu tempat.",
    create: "Buat website",
    emptyTitle: "Belum ada website",
    emptyBody:
      "Jawab beberapa pertanyaan tentang usaha Anda, dan AI menyusun website lengkap dalam hitungan menit.",
    emptyBodyNoAi:
      "Jawab beberapa pertanyaan tentang usaha Anda, dan website lengkap siap dalam hitungan menit.",
    emptyCta: "Buat website pertama",
    emptyPoints: ["Gratis untuk memulai", "Tanpa perlu keahlian teknis", "Siap tampil di HP"] as const,
    status: {
      draft: "Draft",
      published: "Live",
      suspended: "Ditangguhkan",
      unpublished: "Ada perubahan belum dipublish",
    },
    renewalDue: (date: string) => `Perpanjang sebelum ${date}`,
    expired: "Paket berakhir",
    actions: {
      edit: "Edit",
      view: "Lihat",
      domain: "Domain",
      stats: "Statistik",
      settings: "Pengaturan",
      delete: "Hapus website",
    },
    copyUrl: "Salin alamat website",
    notPublished: "Belum dipublish",
    updated: (rel: string) => `Diubah ${rel}`,
    deleteTitle: "Hapus website ini?",
    deleteBody: (name: string) =>
      `“${name}” akan dihapus beserta draft, riwayat versi, dan domainnya. Website langsung tidak bisa diakses pengunjung.`,
    deleteConfirm: "Hapus permanen",
    deleted: "Website dihapus.",
    suspendedNote: (reason?: string) =>
      reason ? `Ditangguhkan: ${reason}` : "Website ini ditangguhkan oleh admin.",
    pendingPayment: "Menunggu bayar",
    search: "Cari website",
    noMatch: (q: string) => `Tidak ada website yang cocok dengan “${q}”.`,
    attention: {
      title: "Perlu perhatian",
      payment: (number: string) => `Pesanan ${number} belum dibayar`,
      review: (number: string) => `Bukti transfer ${number} sedang dicek`,
      expired: "Paket sudah berakhir",
      renewal: (date: string) => `Paket berakhir ${date}`,
      unpublished: "Ada perubahan belum dipublish",
      pay: "Bayar",
      status: "Lihat status",
      renew: "Perpanjang",
      publish: "Publish",
    },
  },
  guests: {
    title: "Tautan undangan pribadi",
    body: "Setiap tamu mendapat tautan sendiri, dan sampul undangan menampilkan “Kepada Yth. [nama tamu]”. Daftar tersimpan di akun Anda, jadi bisa dibuka dari perangkat mana pun.",
    notPublished:
      "Website belum dipublish. Tautan baru bisa dibuka tamu setelah Anda menekan Publish di editor.",
    disabled:
      "Paket website ini tidak termasuk tautan undangan pribadi. Daftar tamu tetap bisa dilihat dan dihapus.",
    upgrade: "Lihat paket",
    addTitle: "Tambah tamu",
    namesLabel: "Nama tamu",
    namesHint:
      "Satu tamu per baris. Boleh tambahkan nomor WhatsApp dan grup, dipisah koma: Budi Santoso, 0812-3456-7890, Keluarga",
    namesPlaceholder: "Budi Santoso, 0812-3456-7890, Keluarga\nKeluarga Bapak Ahmad\nRina Wijaya, 0813-1111-2222",
    add: "Tambahkan",
    added: (n: number) => (n ? `${n} tamu ditambahkan` : "Tidak ada tamu baru"),
    addedNotes: (dup: number, invalid: number, over: number) =>
      [
        dup ? `${dup} sudah ada` : "",
        invalid ? `${invalid} tidak valid (nama kosong atau nomor salah)` : "",
        over ? `${over} melewati batas` : "",
      ]
        .filter(Boolean)
        .join(" · "),
    messageLabel: "Pesan WhatsApp",
    messageHint: "{nama} diganti nama tamu, {link} diganti tautan pribadinya.",
    messageSave: "Simpan pesan",
    messageReset: "Pakai pesan bawaan",
    messageSaved: "Pesan disimpan",
    defaultMessage:
      "Kepada Yth.\n{nama}\n\nTanpa mengurangi rasa hormat, kami mengundang Bapak/Ibu/Saudara/i untuk hadir di acara kami. Info lengkap acara ada di tautan berikut:\n{link}\n\nMerupakan suatu kebahagiaan bagi kami apabila Anda berkenan hadir dan memberikan doa restu. Terima kasih.",
    linksTitle: (n: number) => (n ? `${n} tamu` : "Daftar tamu"),
    count: (n: number, limit: number) => `${n} / ${limit.toLocaleString("id-ID")}`,
    sentCount: (sent: number, total: number) => `${sent} dari ${total} sudah dikirim`,
    empty: "Belum ada tamu. Tambahkan nama di sebelah kiri untuk membuat tautannya.",
    noMatch: "Tidak ada tamu yang cocok.",
    search: "Cari nama",
    allGroups: "Semua grup",
    unsentOnly: "Belum dikirim",
    copyAll: "Salin semua pesan",
    copyMessage: "Salin pesan",
    sendWa: "Kirim WA",
    sent: "Terkirim",
    markSent: "Tandai terkirim",
    markUnsent: "Tandai belum dikirim",
    edit: "Ubah",
    remove: "Hapus",
    removed: "Tamu dihapus",
    actions: (name: string) => `Pilihan untuk ${name}`,
    editTitle: "Ubah tamu",
    nameLabel: "Nama",
    phoneLabel: "Nomor WhatsApp (opsional)",
    phoneHint: "Kirim WA langsung membuka chat dengan nomor ini.",
    groupLabel: "Grup (opsional)",
    groupHint: "Misalnya Keluarga, Teman kuliah, Kantor.",
    saved: "Tamu disimpan",
    localFound: (n: number) => `Ada ${n} nama yang dulu tersimpan di perangkat ini.`,
    localImport: "Pindahkan ke daftar",
    localDismiss: "Abaikan",
  },
  blog: {
    title: "Blog",
    body: "Tulis artikel, kabar, atau tips. Artikel tampil di /blog website Anda dan bisa dimunculkan di beranda lewat section Artikel Terbaru.",
    newPost: "Tulis artikel",
    empty: "Belum ada artikel. Mulai dengan menulis kabar terbaru atau tips untuk pelanggan Anda.",
    published: "Terbit",
    draft: "Draf",
    publishedCount: (n: number, limit: number) =>
      limit >= 1000 ? `${n} artikel terbit` : `${n} dari ${limit} artikel terbit`,
    locked:
      "Paket website ini belum termasuk blog. Anda tetap bisa menulis draf; artikel terbit setelah paket ditingkatkan.",
    updated: (when: string) => `Diubah ${when}`,
    publishedOn: (when: string) => `Terbit ${when}`,
    view: "Lihat",
    back: "Kembali ke Blog",
    titlePlaceholder: "Judul artikel",
    bodyPlaceholder: "Mulai menulis…",
    save: "Simpan",
    saved: "Tersimpan",
    unsaved: "Belum disimpan",
    saving: "Menyimpan…",
    publish: "Terbitkan",
    republish: "Terbitkan perubahan",
    unpublish: "Jadikan draf",
    publishedToast: "Artikel terbit",
    unpublishedToast: "Artikel dijadikan draf",
    remove: "Hapus artikel",
    removeConfirm: "Hapus artikel ini? Tindakan ini tidak bisa dibatalkan.",
    removed: "Artikel dihapus",
    status: "Status",
    slug: "Alamat artikel",
    slugHint: (url: string) => url,
    excerpt: "Ringkasan",
    excerptHint: "Tampil di daftar artikel. Kosongkan untuk memakai kalimat pertama artikel.",
    cover: "Gambar sampul",
    seo: "SEO",
    seoTitle: "Judul di Google",
    seoDescription: "Deskripsi di Google",
    leaveConfirm: "Ada perubahan yang belum disimpan. Tinggalkan halaman ini?",
    toolbar: {
      label: "Format teks",
      h2: "Subjudul",
      h3: "Subjudul kecil",
      bold: "Tebal",
      italic: "Miring",
      bullet: "Daftar",
      ordered: "Daftar bernomor",
      quote: "Kutipan",
      rule: "Garis pemisah",
      link: "Tautan",
      linkPrompt: "Alamat tautan (https://…)",
      linkInvalid: "Tautan harus diawali https://, http://, mailto: atau tel:",
      image: "Gambar",
      undo: "Urungkan",
      redo: "Ulangi",
    },
  },
  responses: {
    rsvpTitle: "Konfirmasi kehadiran",
    wishesTitle: "Ucapan & doa",
    attendance: { yes: "Hadir", no: "Tidak hadir", maybe: "Masih ragu" },
    paxTotal: (n: number) => `${n} orang`,
    pax: (n: number) => `${n} orang`,
    onGuestList: "Dari daftar tamu",
    rsvpEmpty: "Belum ada konfirmasi. Jawaban tamu dari section RSVP di website akan muncul di sini.",
    wishesEmpty: "Belum ada ucapan. Ucapan tamu dari section Ucapan & Doa di website akan muncul di sini.",
    wishesCount: (n: number, limit: number) => `${n} dari ${limit.toLocaleString("id-ID")} ucapan`,
    rsvpLocked:
      "Paket website ini tidak termasuk RSVP, jadi formulirnya tidak tampil di website. Jawaban yang sudah masuk tetap bisa dilihat.",
    wishesLocked: "Paket website ini tidak termasuk Ucapan & Doa, jadi section-nya tidak tampil di website.",
    exportCsv: "Unduh CSV",
    csvHeader: ["Nama", "Kehadiran", "Jumlah orang", "Catatan", "Dari daftar tamu", "Diperbarui"],
    yes: "Ya",
    hidden: "Disembunyikan",
    hide: "Sembunyikan dari website",
    show: "Tampilkan di website",
    removed: "Dihapus",
  },
  overview: {
    premium: {
      title: "Template premium belum dibuka",
      body: "Anda bisa mengedit sepuasnya sekarang. Buka sekali sebelum publish, berlaku selamanya untuk website ini.",
      kind: { template: "Template", design: "Tema" },
      claimFree: "Klaim gratis",
      unlockWithPromo: "Buka dengan voucher",
      unlock: (price: string) => `Buka sekarang · ${price}`,
      once: "Bayar sekali, tanpa biaya bulanan.",
    },
    tabs: {
      ringkasan: "Ringkasan",
      domain: "Domain & paket",
      statistik: "Statistik",
      riwayat: "Riwayat",
      undangan: "Tamu",
      ucapan: "RSVP & ucapan",
      blog: "Blog",
      pengaturan: "Pengaturan",
    },
    /** Bottom bar on phones: five items share 375px. */
    tabsShort: { domain: "Domain", ucapan: "RSVP" },
    tabsLabel: "Bagian website",
    more: "Lainnya",
    moreTitle: "Menu lainnya",
    openSiteShort: "Buka",
    shareMenu: "Bagikan",
    payment: {
      title: (number: string) => `Pesanan ${number} belum dibayar`,
      titleReview: (number: string) => `Pembayaran ${number} sedang dicek`,
      open: "Selesaikan pembayaran agar paket atau domain aktif.",
      awaiting_transfer: "Transfer lalu upload bukti agar paket atau domain aktif.",
      submitted: "Bukti transfer sedang dicek admin. Paket aktif setelah disetujui.",
      rejected: "Bukti transfer ditolak. Lihat alasannya lalu upload ulang.",
      cta: "Bayar sekarang",
      ctaUpload: "Upload bukti",
      ctaStatus: "Lihat status",
      finish: "Selesaikan pembayaran",
    },
    edit: "Edit website",
    siteCard: "Website Anda",
    live: "Website sudah tayang",
    liveHint: "Bagikan alamat website ke pelanggan lewat WhatsApp, media sosial, atau kode QR.",
    liveHintEvent: "Bagikan alamat undangan ke tamu lewat WhatsApp, media sosial, atau kode QR.",
    setup: {
      title: "Siapkan website",
      titleEvent: "Siapkan undangan",
      progress: (done: number, total: number) => `${done} dari ${total} selesai`,
      content: {
        title: "Ganti teks contoh",
        body: (n: number) => `${n} bagian masih berisi contoh dari template, misalnya nama, rekening, atau alamat.`,
        cta: "Perbaiki",
      },
      whatsapp: {
        title: "Tambahkan nomor WhatsApp",
        body: "Tombol pesan di website butuh nomor Anda.",
        bodyEvent: "Tamu bisa konfirmasi kehadiran lewat tombol WhatsApp.",
        cta: "Isi nomor",
      },
      publish: {
        title: "Publish website",
        titleEvent: "Publish undangan",
        body: "Setelah dipublish, alamatnya bisa dibuka siapa saja.",
        cta: "Publish",
      },
      invite: {
        title: "Kirim undangan ke tamu",
        body: "Buat tautan pribadi untuk setiap tamu, lalu kirim lewat WhatsApp.",
        cta: "Kirim undangan",
      },
      done: "Selesai",
    },
    event: {
      label: "Hari acara",
      daysLeft: (n: number) => `${n} hari lagi`,
      today: "Hari ini",
      past: "Acara sudah lewat",
      noDate: "Tanggal acara belum diisi",
      setDate: "Isi tanggal",
      invites: "Undangan terkirim",
      invitesCta: "Kirim undangan",
      attending: "Hadir",
      pax: (n: number) => `${n} orang`,
      notAttending: "Tidak hadir",
      wishes: "Ucapan",
      rsvpCta: "Lihat RSVP & ucapan",
    },
    activity: {
      title: "Aktivitas terbaru",
      yes: (name: string, pax: number) => `${name} akan hadir · ${pax} orang`,
      no: (name: string) => `${name} tidak bisa hadir`,
      maybe: (name: string) => `${name} masih ragu`,
      wish: (name: string) => `${name} mengirim ucapan`,
      empty: "Belum ada aktivitas. Konfirmasi kehadiran dan ucapan tamu muncul di sini.",
      seeAll: "Lihat semua",
    },
    openSite: "Buka website",
    share: "Bagikan ke WhatsApp",
    shareMessage: (name: string, url: string) => `Kunjungi website ${name}: ${url}`,
    qr: "Kode QR",
    qrTitle: "Kode QR website",
    qrBody: "Cetak dan tempel di kasir atau kemasan agar pelanggan mudah membuka website Anda.",
    qrDownload: "Unduh QR (SVG)",
    last7: "7 hari terakhir",
    views: "Kunjungan",
    visitors: "Pengunjung",
    waClicks: "Klik WhatsApp",
    linkClicks: "Klik tautan",
    seeStats: "Lihat statistik",
    accountCard: "Domain & paket",
    domainSection: "Domain",
    domainManage: "Kelola domain",
    planSection: "Paket",
    domainNone: "Belum ada domain sendiri",
    domainConnect: "Hubungkan domain",
    domainFreeTitle: "Subdomain gratis",
    domainLockedCta: "Lihat pilihan domain",
    planRenew: "Perpanjang",
    planUpgrade: "Upgrade paket",
    planUpgradeTo: (plan: string) => `Upgrade ke ${paket(plan)}`,
    planInCart: "Lihat keranjang",
    planExpires: (d: string) => `Berlaku sampai ${d}`,
    planFree: "Gratis selamanya",
    pendingPromo: (code: string) => `Promo ${code} masih bisa Anda klaim`,
    planFreeNote: (plan: string, price: string) =>
      `Upgrade ke ${paket(plan)} (${price}/tahun) untuk memakai domain sendiri dan menghilangkan footer BazeBuild.`,
    planFreeNoteEvent: (plan: string, price: string, months: number) =>
      `Upgrade ke ${paket(plan)} (${price}, sekali bayar untuk ${months} bulan) untuk RSVP, lebih banyak ucapan, dan tanpa footer BazeBuild.`,
    nextSteps: "Langkah berikutnya",
    steps: {
      domainReady: {
        title: (plan: string) => `${paket(plan)} sudah di keranjang`,
        body: (plan: string) =>
          `Pilih nama domain, lalu bayar bersamaan dengan ${paket(plan)}. Domain dipasang setelah pembayaran.`,
        cta: "Pilih domain",
      },
      publish: {
        title: "Publish perubahan",
        body: "Ada perubahan yang belum terlihat pengunjung.",
        cta: "Publish",
      },
    },
    unpublished: "Ada perubahan belum dipublish",
    publishedAt: (rel: string) => `Dipublish ${rel}`,
  },
  analytics: {
    title: "Statistik",
    range: "Rentang",
    ranges: { 7: "7 hari", 30: "30 hari", 90: "90 hari", 365: "1 tahun" } as Record<number, string>,
    rangeLocked: (plan: string) => `Perlu upgrade ke ${paket(plan)}`,
    upsellTitle: "Lihat statistik lebih lama",
    upsellBody: (days: number) =>
      `Paket Anda saat ini menyimpan statistik ${days} hari. Upgrade paket untuk melihat tren yang lebih panjang.`,
    upsellCta: "Upgrade paket",
    upsellCtaTo: (plan: string) => `Upgrade ke ${paket(plan)}`,
    topPages: "Halaman teratas",
    referrers: "Sumber kunjungan",
    devices: "Perangkat",
    device: { mobile: "HP", tablet: "Tablet", desktop: "Komputer" },
    direct: "Langsung / tidak diketahui",
    empty: "Belum ada kunjungan di rentang ini.",
    emptyHint: "Bagikan tautan website Anda ke pelanggan lewat WhatsApp atau Instagram.",
    chartLabel: (days: number) => `Grafik kunjungan ${days} hari`,
    notPublished: "Statistik mulai tercatat setelah website dipublish.",
  },
  versions: {
    title: "Riwayat versi",
    subtitle:
      "Setiap publish tersimpan. Pulihkan versi lama ke draft kapan saja — website live tidak berubah sampai Anda publish lagi.",
    version: (n: number) => `Versi ${n}`,
    current: "Sedang live",
    by: (name: string) => `oleh ${name}`,
    restore: "Pulihkan ke draft",
    restoreTitle: (n: number) => `Pulihkan versi ${n} ke draft?`,
    restoreBody:
      "Draft Anda saat ini akan diganti dengan versi ini. Website live tidak berubah sampai Anda publish.",
    restored: (n: number) => `Versi ${n} dipulihkan ke draft.`,
    empty: "Belum ada versi. Versi pertama tersimpan saat Anda publish.",
    openEditor: "Buka editor",
  },
  settings: {
    title: "Pengaturan",
    general: "Umum",
    name: "Nama website",
    slug: "Alamat subdomain",
    slugHint: (domain: string) => `Website Anda: ${domain}`,
    slugChecking: "Memeriksa…",
    slugAvailable: "Tersedia",
    slugTaken: "Sudah dipakai",
    slugReserved: "Tidak bisa dipakai",
    slugInvalid: "Format belum valid",
    slugSuggestion: (s: string) => `Coba: ${s}`,
    slugWarning: "Mengganti alamat membuat tautan lama berhenti bekerja.",
    brief: "Info usaha untuk AI",
    briefHint: "Dipakai AI saat menulis ulang atau membuat ulang konten.",
    briefNoAi: "Info usaha",
    briefHintNoAi: "Catatan tentang usaha Anda, dipakai sebagai isian awal saat checkout.",
    briefEvent: "Info acara untuk AI",
    briefEventNoAi: "Info acara",
    briefHintEventNoAi: "Catatan tentang acara Anda, dipakai sebagai isian awal saat checkout.",
    city: "Kota",
    address: "Alamat",
    description: "Deskripsi",
    audience: "Target pelanggan",
    style: "Gaya bahasa",
    footer: "Footer",
    keepBranding: "Tampilkan footer BazeBuild",
    keepBrandingOn: "Pengunjung melihat “Dibuat dengan BazeBuild” di bagian bawah website.",
    keepBrandingOff: "Footer disembunyikan. Nyalakan untuk menampilkannya lagi.",
    keepBrandingLocked: "Paket gratis selalu menampilkan footer ini. Upgrade untuk bisa menyembunyikannya.",
    saved: "Pengaturan disimpan.",
    danger: "Zona berbahaya",
    unpublish: "Sembunyikan website",
    unpublishBody: "Website tidak bisa dikunjungi sampai Anda publish lagi. Draft tetap aman.",
    unpublishConfirm: "Sembunyikan",
    unpublished: "Website disembunyikan.",
    deleteBody: "Hapus website beserta semua versinya. Tidak bisa dibatalkan.",
  },
  domains: {
    title: "Domain",
    subtitle: "Pakai alamat sendiri seperti tokoanda.com. SSL (gembok hijau) aktif otomatis.",
    subtitleEvent: "Pakai alamat sendiri seperti alyadandimas.com. SSL (gembok hijau) aktif otomatis.",
    subdomain: "Subdomain gratis",
    existing: "Domain Anda",
    buyTitle: "Beli domain baru",
    buyBody: "Cari nama yang tersedia. Kami daftarkan dan hubungkan otomatis.",
    connectTitle: "Sudah punya domain? Hubungkan",
    connectBody: "Arahkan domain dari registrar Anda (Niagahoster, Rumahweb, dll.).",
    searchPlaceholder: "Cari nama domain, mis. kopisenja",
    searchLabel: "Cari domain",
    searching: "Mencari…",
    searchEmpty: "Ketik nama usaha Anda untuk melihat domain yang tersedia.",
    exact: "Hasil pencarian",
    suggestions: "Pilihan lain",
    available: "Tersedia",
    taken: "Sudah dipakai",
    premium: "Premium",
    needsDocs: "Butuh dokumen",
    estimated: "perkiraan",
    estimatedHint: "Harga perkiraan — harga final dihitung saat checkout.",
    renewal: (p: string) => `perpanjangan ${p}`,
    perYear: (p: string) => `${p}/tahun`,
    addToCart: "Tambah ke keranjang",
    takeFree: "Ambil gratis",
    inCart: "Di keranjang",
    addedToCart: (d: string) => `${d} ditambahkan ke keranjang.`,
    hostnameLabel: "Nama domain",
    hostnamePlaceholder: "tokoanda.com",
    connect: "Hubungkan",
    connecting: "Menghubungkan…",
    stepper: ["DNS", "Verifikasi", "SSL", "Live"] as const,
    recordsTitle: "Tambahkan record DNS ini di registrar Anda",
    recordsBody:
      "Buka panel DNS di tempat Anda membeli domain, lalu tambahkan record berikut. Perubahan DNS bisa memakan waktu hingga beberapa jam.",
    recordType: "Tipe",
    recordHost: "Host / Nama",
    recordValue: "Nilai / Target",
    recordStatus: {
      ok: "Sudah benar",
      missing: "Belum ditemukan",
      wrong: "Nilainya salah",
      unknown: "Belum dicek",
    },
    recordPurpose: { ownership: "Bukti kepemilikan", routing: "Mengarahkan ke website" },
    checkNow: "Cek sekarang",
    checking: "Mengecek…",
    lastChecked: (rel: string) => `Dicek ${rel}`,
    autoCheck: "Kami cek otomatis setiap 20 detik selama halaman ini terbuka.",
    tipsTitle: "Panduan per registrar",
    tips: {
      niagahoster: {
        name: "Niagahoster",
        steps: [
          "Masuk ke Member Area → Domain → pilih domain.",
          "Buka tab “DNS Management” / “Kelola DNS”.",
          "Tambahkan record sesuai tabel di atas, lalu simpan.",
        ],
      },
      rumahweb: {
        name: "Rumahweb",
        steps: [
          "Masuk ke Clientzone → Domain → Kelola Domain.",
          "Pilih “DNS Management”.",
          "Tambahkan record sesuai tabel, pastikan tidak ada record A/CNAME lama yang bentrok.",
        ],
      },
      cloudflare: {
        name: "Cloudflare",
        steps: [
          "Buka dashboard Cloudflare → pilih domain → DNS → Records.",
          "Tambahkan record sesuai tabel.",
          "Matikan proxy: klik awan oranye sampai menjadi abu-abu (DNS only).",
        ],
      },
    },
    status: {
      pending: "Menunggu DNS",
      verifying: "Memverifikasi",
      active: "Aktif",
      failed: "Gagal",
      registering: "Sedang didaftarkan",
    },
    purchasedPreparing: "Sedang disiapkan otomatis",
    primary: "Utama",
    makePrimary: "Jadikan utama",
    madePrimary: (d: string) => `${d} sekarang alamat utama.`,
    removeTitle: (d: string) => `Hapus ${d}?`,
    removeBody:
      "Pengunjung yang membuka domain ini tidak akan diarahkan ke website Anda lagi. Domain yang dibeli tetap milik Anda sampai masa berlakunya habis.",
    removed: "Domain dihapus.",
    expires: (d: string) => `Berlaku sampai ${d}`,
    sslReady: "SSL aktif",
    sslPending: "Menyiapkan SSL",
    added: "Domain ditambahkan. Ikuti langkah DNS di bawah.",
    verifiedToast: (d: string) => `${d} sudah aktif!`,
    upsellTitle: "Perlu upgrade untuk domain sendiri",
    upsellBody: (plan: string, price: string) =>
      `Saat ini website memakai subdomain gratis. Untuk membeli domain baru atau menghubungkan domain yang sudah Anda punya, perlu upgrade ke ${paket(plan)} (${price}/tahun) untuk website ini.`,
    upsellCta: (plan: string) => `Upgrade ke ${paket(plan)}`,
    buyLockedTitle: "Pembelian domain terkunci",
    buyLockedBody: (plan: string, price: string) =>
      `Anda bisa cek nama dan harga sekarang. Domain sendiri perlu ${paket(plan)} (${price}/tahun), jadi paket ini ikut masuk keranjang saat Anda menambahkan domain.`,
    buyLockedReady: (plan: string) =>
      `${paket(plan)} sudah di keranjang. Domain yang dipilih dibayar bersamaan, lalu dipasang setelah pembayaran.`,
    connectLockedTitle: "Penyambungan domain terkunci",
    connectLockedBody: (plan: string) =>
      `Menghubungkan domain sendiri perlu upgrade ke ${paket(plan)}. Form ini aktif setelah paket dibayar; subdomain gratis tetap berjalan.`,
    addWithPlan: "Tambah dengan paket",
    addedWithPlan: (domain: string, plan: string) => `${domain} dan ${paket(plan)} masuk keranjang.`,
    lastError: (e: string) => `Kendala terakhir: ${e}`,
    guide: "Lihat panduan",
    emptyList: "Belum ada domain sendiri. Website Anda tetap bisa diakses lewat subdomain gratis.",
    rateLimited: "Tunggu sebentar sebelum mengecek lagi.",
    dns: {
      manage: "Atur DNS",
      title: (d: string) => `DNS ${d}`,
      body: (d: string) =>
        `Arahkan subdomain ${d} ke aplikasi atau server lain, misalnya shop → toko online Anda. Website Baze tetap di ${d} dan www.`,
      add: "Tambah record",
      addTitle: "Tambah record DNS",
      editTitle: "Ubah record DNS",
      edit: "Ubah",
      type: "Tipe",
      name: "Nama",
      nameHint: (fqdn: string) => `Alamat: ${fqdn}`,
      contentLabel: {
        A: "Alamat IPv4",
        AAAA: "Alamat IPv6",
        CNAME: "Target",
        TXT: "Isi",
        MX: "Server email",
      } as Record<string, string>,
      contentPlaceholder: {
        A: "203.0.113.10",
        AAAA: "2001:db8::1",
        CNAME: "cname.vercel-dns.com",
        TXT: "v=spf1 include:_spf.google.com ~all",
        MX: "smtp.google.com",
      } as Record<string, string>,
      contentHint: {
        A: "IP server aplikasi Anda.",
        AAAA: "IPv6 server aplikasi Anda.",
        CNAME: "Hostname dari penyedia aplikasi (Vercel, Netlify, Shopify, dll.).",
        TXT: "Untuk verifikasi (Google, Meta) atau SPF/DKIM email.",
        MX: "Server email dari penyedia email Anda.",
      } as Record<string, string>,
      priority: "Prioritas",
      ttl: "TTL",
      ttlAuto: "Otomatis",
      ttlMinutes: (n: number) => `${n} menit`,
      ttlHours: (n: number) => `${n} jam`,
      ttlDays: (n: number) => `${n} hari`,
      proxy: "Proxy Cloudflare",
      proxyHint:
        "Lindungi dan percepat lewat Cloudflare (SSL dari Cloudflare). Matikan jika aplikasi Anda mengurus SSL sendiri.",
      proxied: "Proxy aktif",
      dnsOnly: "DNS saja",
      locked: "Dikelola Baze",
      lockedHint: "Record ini dipakai Baze untuk website Anda dan tidak bisa diubah.",
      created: "Record DNS ditambahkan.",
      updated: "Record DNS diperbarui.",
      removed: "Record DNS dihapus.",
      removeTitle: (type: string, name: string) => `Hapus record ${type} ${name}?`,
      removeBody: "Layanan yang memakai record ini akan berhenti bisa diakses lewat alamat tersebut.",
      propagation: "Perubahan biasanya aktif dalam beberapa menit, kadang hingga 1 jam.",
      upsellTitle: "Atur DNS tersedia di paket lebih tinggi",
      upsellBody: (plan: string, price: string) =>
        `Untuk mengarahkan subdomain (mis. shop atau api) ke aplikasi lain, upgrade website ini ke ${paket(plan)} (${price}/tahun).`,
      upsellCta: (plan: string) => `Upgrade ke ${paket(plan)}`,
    },
  },
  cart: {
    title: "Keranjang website ini",
    forSite: (name: string) => `Untuk ${name}`,
    open: "Buka keranjang",
    count: (n: number) => `${n} item di keranjang`,
    empty: "Keranjang masih kosong",
    emptyBody: "Tambahkan Paket Website, domain, atau tema premium untuk website ini.",
    browsePlans: "Lihat Paket Website",
    remove: (label: string) => `Hapus ${label} dari keranjang`,
    short: "Bayar",
    checkout: "Lanjut ke pembayaran",
    checkoutPriced: (price: string) => `Lanjut bayar · ${price}`,
    subtotal: "Subtotal",
    estimate: "Harga final dihitung di halaman pembayaran.",
    kinds: {
      template: "Template",
      design: "Tema premium",
      site_plan: "Paket Website",
      domain_register: "Domain baru",
      domain_renew: "Perpanjang domain",
    },
    planName: (name: string) => paket(name),
    needsPlanForDomain: "Domain sendiri dipasang setelah Paket Website ikut terbayar.",
    addRecommendedPlan: (name: string) => `Tambah ${paket(name)}`,
    added: "Ditambahkan ke keranjang.",
  },
  library: {
    nav: "Template saya",
    title: "Template saya",
    subtitle: "Template favorit, yang sudah dibeli, dan yang sedang dipakai website Anda.",
    favorite: (name: string) => `Simpan ${name} ke favorit`,
    unfavorite: (name: string) => `Hapus ${name} dari favorit`,
    favoriteShort: "Simpan ke favorit",
    unfavoriteShort: "Hapus dari favorit",
    tabs: { favorites: "Favorit", purchases: "Dibeli", used: "Dipakai" },
    kind: { template: "Template", design: "Tema" },
    emptyFavorites: "Belum ada favorit",
    emptyFavoritesBody:
      "Tekan ikon hati pada template atau tema saat membuat website untuk menyimpannya di sini.",
    emptyPurchases: "Belum ada pembelian",
    emptyPurchasesBody: "Template dan tema premium yang Anda buka akan muncul di sini.",
    emptyUsed: "Belum ada website",
    emptyUsedBody: "Template yang dipakai website Anda akan muncul di sini.",
    boughtFor: (site: string) => `Dibuka untuk ${site}`,
    usedBy: (site: string) => `Dipakai ${site}`,
    useTemplate: "Pakai template",
    openSite: "Buka website",
    browse: "Buat website baru",
    owned: "Sudah dimiliki",
    themeWith: (theme: string) => `Tema ${theme}`,
  },
  claim: {
    docTitle: (plan: string) => `Klaim ${plan}`,
    badge: "Promo terbatas",
    endsIn: (n: number, unit: "hari" | "jam") => `Berakhir dalam ${n} ${unit}`,
    endsToday: "Berakhir hari ini",
    title: (plan: string, pct: number) => `Paket ${plan}, hemat ${pct}%`,
    titleFlat: (plan: string) => `Paket ${plan} lebih hemat`,
    lead: "Klaim harganya sekarang, buat website kapan saja. Paketnya langsung aktif di website Anda.",
    bonusKicker: "Bonus",
    bonusTitle: (tld: string) => `Domain sendiri .${tld} gratis`,
    bonusExample: "namausahamu",
    bonusSteps: [
      "Buat website Anda setelah bayar",
      "Pilih nama domain di tab Domain, aktif tanpa bayar lagi",
    ] as const,
    getTitle: "Yang Anda dapat",
    howTitle: "Cara kerjanya",
    steps: [
      { title: "Klaim & bayar", body: "Daftar atau masuk, lalu bayar dalam satu langkah." },
      { title: "Buat website kapan saja", body: "Pilih template dan isi info usaha, cukup 5 menit." },
      { title: "Paket langsung aktif", body: "Otomatis di website Anda, tanpa langkah tambahan." },
    ] as const,
    noSiteNeeded: "Bayar sekarang, atau coba buat website gratis dulu",
    tryFree: "Coba buat website gratis dulu",
    tryFreeNote: "Diskonnya tetap bisa Anda klaim nanti selama promo berlaku.",
    summaryPlan: (plan: string) => `Paket ${plan}`,
    oneYear: "1 tahun",
    firstYear: "tahun pertama",
    renewal: (price: string) => `Perpanjangan ${price}/tahun`,
    domainLine: (tld: string) => `Domain .${tld} · 1 tahun`,
    receiptPlan: (plan: string) => `Paket ${plan} · 1 tahun`,
    receiptDiscount: "Diskon promo",
    receiptTotal: "Total bayar",
    signedInAs: (email: string) => `Masuk sebagai ${email}.`,
    notYou: "Bukan Anda?",
    faqTitle: "Pertanyaan umum",
    faq: [
      {
        q: "Kapan paketnya aktif?",
        a: "Begitu pembayaran berhasil, paket tersimpan di akun Anda dan langsung aktif di website pertama yang Anda buat.",
      },
      {
        q: "Saya sudah punya website, bagaimana?",
        a: "Setelah bayar, Anda bisa memilih website mana yang dijadikan Profesional dari halaman Website saya.",
      },
      {
        q: "Kapan saya memilih nama domain?",
        a: "Setelah website jadi. Buka tab Domain di website Anda, cari nama yang diinginkan, dan tahun pertamanya gratis.",
      },
    ] as const,
    free: "Gratis",
    off: (pct: number) => `−${pct}%`,
    save: (v: string) => `Hemat ${v}`,
    quota: (left: number, max: number) => `Sisa ${left} dari ${max} kuota`,
    quotaLow: (left: number) => `Tinggal ${left} kuota lagi!`,
    claim: (pct: number) => (pct > 0 ? `Klaim diskon ${pct}%` : "Klaim promo"),
    claimShort: "Klaim sekarang",
    haveAccount: "Sudah punya akun?",
    login: "Masuk",
    guestNote: "Daftar gratis dalam 30 detik. Bayar sekarang atau coba buat website dulu.",
    pay: (amount: string) => `Bayar ${amount}`,
    paying: "Memproses…",
    methodTitle: "Metode pembayaran",
    trust: ["Pembayaran aman", "Aktif otomatis", "Bantuan via WhatsApp"] as const,
    askWa: "Tanya via WhatsApp",
    waMessage: (code: string) => `Halo Baze, saya mau tanya soal promo ${code}.`,
    usedTitle: "Promo ini sudah Anda klaim",
    usedBody: "Paketnya tersimpan di akun Anda dan aktif otomatis di website Anda.",
    usedCta: "Ke Website saya",
    endedTitle: "Promonya sudah berakhir",
    endedCta: "Lihat paket lainnya",
    notFoundTitle: "Promo tidak ditemukan",
    notFoundBody: "Tautan promo ini salah atau sudah tidak berlaku.",
    // Success + credits
    readyTitle: (plan: string) => `Paket ${plan} siap dipakai!`,
    readyNoSite: "Buat website Anda sekarang. Paketnya langsung aktif begitu website jadi.",
    readyPick: "Pilih website yang mau dijadikan Profesional, atau buat website baru.",
    createSite: "Buat website sekarang",
    applyTo: "Pakai di website ini",
    applyTitle: "Pakai di website",
    applied: "Paket diaktifkan di website Anda.",
    bannerTitle: (plan: string, n: number) =>
      n > 1 ? `Anda punya ${n} Paket ${plan} yang belum dipakai` : `Paket ${plan} Anda belum dipakai`,
    bannerNoSite: "Buat website pertama Anda, paketnya langsung aktif.",
    bannerPick: "Pilih website untuk mengaktifkannya.",
    domainBanner: (tld: string) => `Domain .${tld} gratis menunggu Anda`,
    domainBannerBody: "Cari nama yang Anda mau, tahun pertama gratis tanpa pembayaran.",
    domainBannerCta: "Pilih nama domain",
    domainFreeBadge: "Gratis tahun pertama",
    nextDomain: (tld: string) => `Langkah berikutnya: pilih nama domain .${tld} gratis Anda`,
    wizardNote: (plan: string) => `Paket ${plan} Anda akan aktif otomatis di website ini.`,
    authBanner: "Satu langkah lagi untuk klaim promo Anda. Setelah daftar, Anda kembali ke halaman promo.",
  },
  promo: {
    free: "Gratis",
    useCode: (code: string) => `Pakai voucher ${code}`,
    claimFree: "Klaim gratis",
    applyAndPay: "Pakai & lanjut",
    suggestTitle: "Promo tersedia untuk pesanan ini",
    apply: "Pakai",
  },
  checkout: {
    title: "Pembayaran",
    subtitle: (name: string) => `Untuk website ${name}`,
    summary: "Ringkasan pesanan",
    planTitle: "Paket Website",
    planSubtitle:
      "Dibayar per tahun untuk website ini. Nama domain tidak termasuk — itu dibeli terpisah, satu untuk tiap website.",
    planSubtitleEvent: "Sekali bayar untuk undangan ini, tanpa perpanjangan. Nama domain tidak termasuk.",
    benefitGuestLinks: "Link undangan per nama tamu",
    benefitGuestbook: (n: number) => `Ucapan & doa (${n.toLocaleString("id-ID")} ucapan)`,
    benefitRsvp: "RSVP konfirmasi kehadiran",
    benefitBlog: (n: number) => (n >= 1000 ? "Blog tanpa batas" : `Blog (${n} artikel)`),
    benefitActive: (months: number) => `Aktif ${months} bulan`,
    oneTime: "sekali bayar",
    forMonths: (months: number) => `untuk ${months} bulan`,
    benefitDomain: "Bisa pasang domain sendiri + SSL",
    benefitSubdomain: "Alamat subdomain gratis",
    benefitNoBrand: "Tanpa footer BazeBuild",
    benefitBrand: "Dengan footer BazeBuild",
    benefitDns: "Atur DNS domain (subdomain ke aplikasi lain)",
    benefitPages: (n: number) => `${n} halaman`,
    benefitAi: (n: number) => `${n} generate AI / bulan`,
    benefitRewrite: (n: number) => `${n} tulis ulang AI / bulan`,
    benefitStorage: (size: string) => `Penyimpanan foto ${size}`,
    benefitStats: (period: string) => `Statistik pengunjung ${period}`,
    benefitYear: "1 tahun",
    benefitDays: (n: number) => `${n} hari`,
    planNone: "Tetap Gratis",
    planCurrent: "Paket saat ini",
    planActiveUntil: (d: string) => `aktif sampai ${d}`,
    planKeep: "Tanpa biaya",
    planKeepHint: "Paket Anda sudah aktif, jadi tidak ada tagihan paket. Perlu memperpanjang?",
    planRenew: "Perpanjang paket ini",
    planRenewNote: "Paket diperpanjang dari tanggal berakhirnya, jadi sisa masa aktif tidak hangus.",
    planRenewCancel: "Batal",
    duration: "Durasi pembayaran",
    durationHint: "Bisa dibayar 1–3 tahun sekaligus.",
    forever: "Tanpa biaya tahunan",
    save: (pct: number) => `Hemat ${pct}%`,
    contactTitle: "Data pemilik domain",
    contactSubtitle: "Wajib untuk mendaftarkan domain. Disimpan aman dan hanya dikirim ke registrar.",
    savedContacts: "Kontak tersimpan",
    newContact: "Kontak baru",
    contact: {
      name: "Nama lengkap",
      email: "Email",
      phone: "Nomor HP",
      organization: "Nama usaha / organisasi",
      address: "Alamat lengkap",
      city: "Kota / kabupaten",
      province: "Provinsi",
      provincePlaceholder: "Pilih provinsi",
      postalCode: "Kode pos",
    },
    total: "Total pembayaran",
    pay: "Bayar sekarang",
    payAmount: (price: string) => `Bayar ${price}`,
    voucher: {
      toggle: "Punya kode voucher?",
      label: "Kode voucher",
      placeholder: "Contoh: GRATISTEMPLATE",
      apply: "Pakai",
      remove: "Hapus voucher",
      subtotal: "Subtotal",
      discount: (code: string) => `Voucher ${code}`,
      claimFree: "Klaim gratis",
      freeNote: "Promo menanggung seluruh biaya. Tidak perlu membayar apa pun.",
    },
    payChoose: "Pilih paket dulu",
    paying: "Memproses…",
    trust: "Pembayaran aman & terenkripsi",
    finalPrice: "Harga final, sudah termasuk biaya layanan.",
    unavailable: "Tidak tersedia",
    removeUnavailable: "Hapus item ini",
    empty: "Belum ada yang perlu dibayar",
    emptyBody: "Pilih paket berbayar di sebelah kiri. Domain dan tema premium juga muncul di sini.",
    backToSite: "Kembali ke website",
    quoteFailed: "Harga belum bisa dihitung.",
    simulationNote: "Mode simulasi aktif — tidak ada uang yang ditarik.",
    gatewayTitle: "Metode pembayaran",
    gatewaySubtitle: "Pilih cara bayar. Anda menyelesaikan pembayaran di halaman berikutnya.",
    methodSearch: "Cari bank, e-wallet, atau QRIS",
    methodClear: "Hapus pencarian",
    methodNone: "Metode tidak ditemukan. Coba kata lain.",
    paidVia: "Dibayar via",
    changeMethod: "Ubah",
    viaShort: (name: string) => `via ${name}`,
    gatewayDummy: "Uji coba. Tidak ada uang yang ditarik.",
    gatewayMidtrans: "QRIS, GoPay, virtual account, atau kartu.",
    gatewayQris: "Pindai kode QRIS di aplikasi bank atau e-wallet.",
    gatewayManual: "Transfer ke rekening kami, lalu unggah bukti transfer. Diverifikasi tim kami.",
    recommended: "Rekomendasi",
    methodsManual: "Transfer bank · diverifikasi manual",
    snapLoading: "Membuka pembayaran…",
    snapFailed: "Jendela pembayaran gagal dibuka. Coba lagi.",
    methods: "QRIS · GoPay · Virtual Account · Kartu",
    methodsQris: "QRIS",
    methodsDummy: "Simulasi — tidak ada uang yang ditarik",
    itemYears: (n: number) => `${n} tahun`,
  },
  payment: {
    voucherSaved: (code: string, amount: string) => `Hemat ${amount} dengan voucher ${code}`,
    title: "Status pembayaran",
    order: (n: string) => `Pesanan ${n}`,
    pending: "Menunggu pembayaran",
    pendingBody: "Selesaikan pembayaran. Halaman ini diperbarui otomatis.",
    pendingSnap: "Selesaikan pembayaran di jendela Midtrans. Halaman ini diperbarui otomatis.",
    pendingDuitku:
      "Selesaikan pembayaran lewat halaman Duitku. Halaman ini diperbarui otomatis setelah pembayaran masuk.",
    pendingQris: "Selesaikan pembayaran QRIS. Halaman ini diperbarui otomatis setelah pembayaran masuk.",
    pendingDummy: "Buka halaman simulasi untuk memilih hasil pembayaran. Tidak ada uang yang ditarik.",
    pendingManual: "Transfer ke salah satu rekening di bawah, lalu unggah bukti transfernya.",
    pendingManualRejected:
      "Bukti transfer sebelumnya belum bisa kami terima. Periksa alasannya lalu unggah ulang.",
    pendingManualReview:
      "Bukti transfer Anda sedang kami periksa. Halaman ini diperbarui otomatis setelah diverifikasi.",
    verifying: "Menunggu verifikasi",
    manual: {
      stepTransfer: "Transfer",
      stepUpload: "Unggah bukti",
      stepVerify: "Verifikasi",
      stepsLabel: "Langkah pembayaran transfer bank",
      amountTitle: "Transfer tepat sejumlah",
      amountHint: "Nominal yang sama persis mempercepat verifikasi.",
      copyAmount: "Salin nominal",
      amountCopied: "Nominal disalin.",
      accountsTitle: "Rekening tujuan",
      accountsNone: "Rekening tujuan belum tersedia. Hubungi kami untuk instruksi pembayaran.",
      accountNumber: "Nomor rekening",
      holder: (name: string) => `a.n. ${name}`,
      copyNumber: "Salin nomor rekening",
      numberCopied: "Nomor rekening disalin.",
      referenceTip: (n: string) => `Tulis ${n} di berita/keterangan transfer agar mudah kami cocokkan.`,
      copyReference: "Salin nomor pesanan",
      referenceCopied: "Nomor pesanan disalin.",
      instructions: "Petunjuk",
      deadline: (d: string) => `Selesaikan transfer sebelum ${d}`,
      uploadTitle: "Unggah bukti transfer",
      reuploadTitle: "Unggah ulang bukti transfer",
      uploadSubtitle:
        "Foto atau screenshot struk/notifikasi transfer yang menampilkan nominal, tanggal, dan rekening tujuan.",
      dropTitle: "Pilih atau seret file ke sini",
      dropHint: "Foto/screenshot (dikompres otomatis, tetap jelas) atau PDF maks. 5 MB",
      fileTooBigRaw: "Foto maksimal 25 MB.",
      compressing: "Mengompres foto…",
      compressed: (from: string, to: string) => `Dikompres ${from} → ${to}, teks tetap jelas`,
      dropActive: "Lepaskan untuk mengunggah",
      replaceFile: "Ganti file",
      removeFile: "Hapus file",
      fileTooBig: "Ukuran file maksimal 5 MB.",
      fileType: "Gunakan foto JPG, PNG, WebP, atau PDF.",
      fileRequired: "Pilih foto atau PDF bukti transfer.",
      payerName: "Nama pemilik rekening pengirim",
      payerNameHint: "Sesuai nama di rekening atau e-wallet yang dipakai.",
      payerBank: "Bank / e-wallet pengirim",
      payerBankPlaceholder: "Contoh: BCA",
      amount: "Nominal yang ditransfer",
      amountMismatch: (total: string) =>
        `Berbeda dari total tagihan ${total}. Pastikan nominalnya benar agar cepat diverifikasi.`,
      transferredAt: "Tanggal transfer",
      note: "Catatan untuk tim kami",
      notePlaceholder: "Contoh: transfer dari rekening istri",
      submit: "Kirim bukti transfer",
      submitting: "Mengunggah…",
      submitted: "Bukti transfer terkirim. Kami akan memeriksanya segera.",
      fixErrors: "Periksa kembali isian yang ditandai.",
      reviewTitle: "Bukti transfer sedang diperiksa",
      reviewBody:
        "Biasanya selesai dalam 1×24 jam kerja. Anda juga akan menerima email setelah diverifikasi.",
      rejectedTitle: "Bukti transfer ditolak",
      rejectedReason: (r: string) => `Alasan: ${r}`,
      sentAt: (d: string) => `Dikirim ${d}`,
      viewFile: "Lihat bukti",
      fileLoading: "Memuat bukti…",
      fileFailed: "Bukti belum bisa ditampilkan.",
      pdf: "Dokumen PDF",
      history: (n: number) => `Riwayat bukti (${n})`,
      proofStatus: { submitted: "Diperiksa", approved: "Diterima", rejected: "Ditolak" },
      cancelLocked: "Pesanan tidak bisa dibatalkan selama bukti transfer diperiksa.",
      required: "Wajib diisi.",
    },
    paid: "Pembayaran berhasil!",
    paidBody: "Terima kasih! Kami sedang mengaktifkan pesanan Anda.",
    fulfilled: "Semua sudah aktif",
    failed: "Pembayaran gagal",
    failedBody: "Tidak ada dana yang ditarik. Silakan coba lagi atau pilih metode lain.",
    expired: "Pembayaran kedaluwarsa",
    expiredBody: "Batas waktu pembayaran habis. Buat pembayaran baru untuk melanjutkan.",
    cancelled: "Pesanan dibatalkan",
    refunded: "Dana dikembalikan",
    retry: "Coba bayar lagi",
    continueEditor: "Lanjut ke editor",
    seeDomain: "Lihat domain",
    seeReceipt: "Lihat kwitansi",
    openPayment: "Buka halaman pembayaran",
    continuePay: "Lanjutkan pembayaran",
    expiresAt: (d: string) => `Bayar sebelum ${d}`,
    timeline: "Proses pesanan",
    step: {
      pending: "Menunggu",
      running: "Sedang diproses",
      done: "Selesai",
      failed: "Gagal",
      needs_attention: "Sedang kami proses",
    },
    needsAttention:
      "Ada langkah yang butuh penanganan tim kami. Kami akan menghubungi Anda — tidak perlu membayar ulang.",
    cancel: "Batalkan pesanan",
    cancelled_toast: "Pesanan dibatalkan.",
    simBanner: "MODE SIMULASI — tidak ada uang yang ditarik",
    simTitle: "Simulasi pembayaran",
    simBody:
      "Pilih hasil pembayaran untuk menguji alur. Di mode live, halaman ini diganti gateway pembayaran asli.",
    simPaid: "Bayar berhasil",
    simFailed: "Gagal",
    simExpired: "Kedaluwarsa",
    simUnavailable: "Halaman ini hanya untuk pesanan simulasi.",
    notFound: "Pesanan tidak ditemukan.",
  },
  orders: {
    title: "Pesanan",
    subtitle: "Riwayat pembelian untuk semua website Anda.",
    empty: "Belum ada pesanan",
    emptyBody: "Pembelian Paket Website, domain, dan tema premium akan muncul di sini.",
    number: "No. pesanan",
    site: "Website",
    date: "Tanggal",
    items: "Item",
    total: "Total",
    receipt: "Kwitansi",
    status: {
      pending: "Menunggu bayar",
      paid: "Lunas",
      failed: "Gagal",
      expired: "Kedaluwarsa",
      cancelled: "Dibatalkan",
      refunded: "Dikembalikan",
    },
    manual: {
      awaiting_transfer: "Perlu bukti transfer",
      submitted: "Sedang diverifikasi",
      rejected: "Bukti ditolak",
      approved: "Terverifikasi",
    },
    detail: "Detail",
    forSite: "Pesanan website ini",
    siteEmpty: "Belum ada pesanan untuk website ini.",
  },
  receipt: {
    subtotal: "Subtotal",
    voucher: (code: string) => `Voucher ${code}`,
    title: "Kwitansi",
    print: "Cetak / simpan PDF",
    back: "Kembali ke pesanan",
    issuedBy: "Diterbitkan oleh",
    billedTo: "Ditagihkan kepada",
    number: "Nomor",
    date: "Tanggal",
    paidAt: "Dibayar",
    item: "Deskripsi",
    qty: "Durasi",
    price: "Harga",
    amount: "Jumlah",
    total: "Total",
    status: "Status",
    note: "Kwitansi ini sah tanpa tanda tangan. Harga sudah termasuk pajak dan biaya layanan.",
    notPaid: "Pesanan ini belum lunas — kwitansi final tersedia setelah pembayaran.",
  },
  account: {
    title: "Akun",
    subtitle: "Kelola profil, keamanan, dan data Anda.",
    profile: "Profil",
    profileSaved: "Profil diperbarui.",
    emailVerified: "Terverifikasi",
    emailUnverified: "Belum diverifikasi",
    security: "Keamanan",
    changePassword: "Ganti kata sandi",
    setPassword: "Buat kata sandi",
    setPasswordHint: "Anda masuk dengan Google. Buat kata sandi agar bisa masuk dengan email juga.",
    currentPassword: "Kata sandi saat ini",
    passwordChanged: "Kata sandi diperbarui. Perangkat lain sudah dikeluarkan.",
    google: "Akun Google",
    googleLinked: "Terhubung",
    googleNotLinked: "Belum terhubung",
    googleLink: "Hubungkan Google",
    sessions: "Perangkat aktif",
    sessionsHint: "Perangkat yang sedang masuk ke akun Anda.",
    thisDevice: "Perangkat ini",
    sessionOn: (browser: string, os: string) => `${browser} di ${os}`,
    lastActive: (rel: string) => `Aktif ${rel}`,
    revoke: "Keluarkan",
    revoked: "Perangkat dikeluarkan.",
    logoutAll: "Keluar dari semua perangkat",
    logoutAllTitle: "Keluar dari semua perangkat?",
    logoutAllBody: "Anda juga akan keluar dari perangkat ini dan perlu masuk lagi.",
    deleteTitle: "Hapus akun",
    deleteBody: "Menghapus akun akan:",
    deleteConsequences: [
      "Menghapus semua website Anda dan membuatnya tidak bisa dikunjungi",
      "Menghentikan domain yang terhubung (domain yang dibeli tetap terdaftar sampai masa berlakunya habis)",
      "Menghapus data pribadi Anda sesuai UU Pelindungan Data Pribadi",
      "Riwayat pesanan disimpan terbatas untuk keperluan pajak",
    ] as const,
    deleteCta: "Hapus akun saya",
    deletePassword: "Masukkan kata sandi untuk konfirmasi",
    deleted: "Akun Anda telah dihapus. Terima kasih sudah memakai Baze.",
  },
  editor: {
    versions: "Versi sebelumnya",
    back: "Kembali",
    pages: "Halaman",
    pageSwitcher: "Pilih halaman",
    addPage: "Tambah halaman",
    pagePresets: { tentang: "Tentang", layanan: "Layanan", kontak: "Kontak", kosong: "Halaman kosong" },
    pageLimit: (n: number) => `Paket Anda maksimal ${n} halaman.`,
    pageLimitCta: "Upgrade paket",
    renamePage: "Ganti nama halaman",
    removePage: "Hapus halaman",
    removePageTitle: (name: string) => `Hapus halaman “${name}”?`,
    removePageBody: "Semua section di halaman ini ikut terhapus. Anda masih bisa mengurungkan.",
    pageActions: "Opsi halaman",
    homeLocked: "Beranda tidak bisa dihapus",
    addPageBody:
      "Halaman baru otomatis muncul di menu website. Isinya langsung terisi contoh yang bisa Anda ubah.",
    pagePresetLabel: "Mulai dari",
    pagePresetHints: {
      tentang: "Cerita, angka, testimoni",
      layanan: "Layanan, harga, FAQ",
      kontak: "Kontak, lokasi, jam buka",
      kosong: "Hanya menu dan footer",
    },
    pageName: "Nama halaman",
    renamePageBody: "Nama ini tampil di menu website.",
    pageAdded: (name: string) => `Halaman “${name}” ditambahkan.`,
    pageRemoved: (name: string) => `Halaman “${name}” dihapus.`,
    home: "Beranda",
    device: { mobile: "HP", tablet: "Tablet", desktop: "Komputer" },
    deviceLabel: "Ukuran layar pratinjau",
    undo: "Urungkan",
    redo: "Ulangi",
    preview: "Pratinjau",
    publish: "Publish",
    unpublishedDot: "Ada perubahan belum dipublish",
    /** Same words as the site list and overview. */
    status: { draft: "Draft", live: "Live", liveChanged: "Live · ada perubahan" },
    upToDate: "Sudah live",
    issues: (n: number) => `${n} bagian perlu dicek`,
    liveBar: {
      draftTitle: "Domain belum bisa dibuka",
      draftBody: (url: string) =>
        `Perubahan tersimpan otomatis sebagai draft. Publish di kanan atas agar ${url} bisa dikunjungi.`,
      pendingTitle: "Perubahan belum tayang",
      pendingBody:
        "Pengunjung masih melihat versi sebelumnya. Publish di kanan atas agar perubahan ini muncul.",
    },
    panels: { outline: "Section", canvas: "Pratinjau", inspect: "Edit" },
    mobile: {
      nav: "Menu editor",
      editSection: "Edit section",
      sheetClose: "Tutup",
      sections: "Bagian",
      add: "Tambah",
      edit: "Edit",
      preview: "Pratinjau",
      previewing: "Mode pratinjau",
      backToEdit: "Kembali edit",
      expand: "Perbesar panel",
      shrink: "Perkecil panel",
      close: "Tutup panel",
      editContent: "Edit isi",
      more: "Lainnya",
      fullPreview: "Pratinjau layar penuh",
      openLive: "Buka website",
      saveNow: "Simpan sekarang",
      saved: "Tersimpan",
      saving: "Menyimpan…",
      unsaved: "Belum tersimpan",
      failed: "Gagal simpan",
      retry: "Ketuk untuk coba simpan lagi",
      dismissBanner: "Tutup pemberitahuan",
      showMore: "Selengkapnya",
      textTitle: (section: string) => `Teks · ${section}`,
      done: "Selesai",
      next: "Teks berikutnya",
      tapHint: "Ketuk teks untuk mengetik, ketuk bagian lain untuk opsi.",
      selected: (section: string) => `${section} dipilih`,
    },
    canvasHint:
      "Klik teks di pratinjau untuk langsung mengetik. Klik bagian lain, seperti foto, untuk pengaturan di panel kanan.",
    inline: {
      label: "Edit teks",
      editing: "Mengedit teks",
      hint: "Enter selesai · Tab berikutnya · Esc batal",
      hintMulti: "Ctrl+Enter selesai · Tab berikutnya · Esc batal",
    },
    whatsappHint: "Diawali 62, tanpa + atau spasi. Contoh: 6281234567890.",
    whatsappInvalid: "Nomor belum valid. Contoh: 6281234567890.",
    requiredField: "Isian ini wajib.",
    invalidField: "Format belum sesuai.",
    shortcuts: "Pintasan keyboard",
    save: {
      saving: "Menyimpan…",
      saved: (rel: string) => `Tersimpan • ${rel}`,
      savedNow: "Tersimpan • baru saja",
      dirty: "Perubahan belum tersimpan",
      offline: "Offline — tersimpan di perangkat",
      error: "Gagal menyimpan — coba lagi",
      conflict: "Konflik — perlu tindakan",
      retry: "Coba simpan lagi",
    },
    leaveWarning: "Perubahan Anda belum tersimpan. Yakin ingin keluar?",
    tabs: { sections: "Section", theme: "Tema", info: "Info bisnis", seo: "SEO", publish: "Publish" },
    tabsShort: { sections: "Section", theme: "Tema", info: "Info", seo: "SEO", publish: "Publish" },
    outline: {
      title: "Section di halaman ini",
      add: "Tambah section",
      drag: (label: string) => `Geser ${label}`,
      hide: "Sembunyikan",
      show: "Tampilkan",
      hidden: "Tersembunyi",
      duplicate: "Duplikat",
      delete: "Hapus",
      moveUp: "Naikkan",
      moveDown: "Turunkan",
      deleted: (label: string) => `${label} dihapus.`,
      issues: (n: number) => `${n} hal perlu diperbaiki`,
      locked: "Posisi tetap",
      dnd: {
        instructions:
          "Tekan spasi atau Enter untuk mengangkat section. Gunakan panah atas/bawah untuk memindahkan, spasi untuk meletakkan, Escape untuk batal.",
        start: (label: string) => `${label} diangkat.`,
        over: (label: string, pos: number, total: number) =>
          `${label} dipindah ke posisi ${pos} dari ${total}.`,
        end: (label: string, pos: number, total: number) =>
          `${label} diletakkan di posisi ${pos} dari ${total}.`,
        cancel: (label: string) => `Pemindahan ${label} dibatalkan.`,
      },
    },
    picker: {
      title: "Tambah section",
      subtitle: "Pilih jenis section. Isinya langsung terisi contoh yang bisa Anda ubah.",
      singletonExists: "Sudah ada di halaman",
      insertHere: "Tambah section di sini",
      added: (label: string) => `${label} ditambahkan.`,
      suggested: "Disarankan",
      /** Invitations and events: the same sections, described for guests instead of customers. */
      eventDescriptions: {
        about: "Cerita singkat tentang kalian atau acaranya.",
        gallery: "Kumpulan foto kenangan.",
        timeline: "Susunan acara atau kisah perjalanan kalian.",
        team: "Kedua mempelai atau tuan rumah acara.",
      } as Partial<Record<string, string>>,
    },
    toolbar: {
      label: "Aksi section",
      moveUp: "Naikkan",
      moveDown: "Turunkan",
      variant: "Ganti gaya",
      duplicate: "Duplikat",
      hide: "Sembunyikan",
      show: "Tampilkan",
      delete: "Hapus",
      ai: "AI",
      aiTitle: "Tulis ulang dengan AI",
    },
    canvas: {
      label: "Pratinjau website",
      empty: "Halaman ini masih kosong.",
      emptyCta: "Tambah section pertama",
      invalid: "Beberapa isian belum valid. Perbaiki di panel kanan — website tetap tersimpan.",
      loadingEngine: "Menyiapkan pratinjau…",
    },
    panel: {
      expand: "Lebarkan",
      shrink: "Kecilkan",
      expandHint: "Panel lebih lebar untuk isi yang panjang",
      shrinkHint: "Kembalikan panel ke ukuran biasa",
      resize: "Ubah lebar panel",
      resizeHint: "Seret untuk mengubah lebar · klik dua kali untuk ukuran awal",
    },
    workspace: {
      settings: "Pengaturan section",
      search: "Cari",
      moveTo: "Pindah ke",
      back: "Kembali ke daftar",
      empty: "Tidak ada yang cocok.",
      untitled: "(belum ada judul)",
      full: "Batas tercapai",
    },
    inspector: {
      inlineTip: "Tips: teks bisa langsung diketik dengan mengekliknya di pratinjau.",
      contentTitle: "Isi",
      appearanceTitle: "Tampilan",
      menuTitle: "Menu atas",
      showInNavHint: "Jadi tautan di menu atas website.",
      hiddenNotInNav: "Section ini disembunyikan, jadi belum tampil di menu.",
      menuPagesHint: "Website ini punya beberapa halaman, jadi menu berisi nama halaman. Ganti nama halaman lewat tombol ··· di Pilih halaman.",
      navMenuTitle: "Isi menu",
      navMenuHint: "Nyalakan section yang mau muncul di menu dan ganti namanya di sini. Urutan menu mengikuti urutan section di halaman.",
      navMenuPagesHint: "Menu berisi halaman website. Ganti namanya di sini; urutan dan halaman baru diatur lewat Pilih halaman.",
      navMenuEmpty: "Belum ada section di menu. Nyalakan minimal satu di bawah ini.",
      noSelection: "Pilih section untuk mengedit",
      noSelectionBody: "Klik bagian mana pun di pratinjau, atau pilih dari daftar section.",
      tipsTitle: "Tips halaman ini",
      tips: [
        "Klik dua kali pada teks di pratinjau untuk langsung mengeditnya.",
        "Tombol WhatsApp paling sering diklik — pastikan nomornya benar.",
        "Foto asli usaha Anda meningkatkan kepercayaan pelanggan.",
      ] as const,
      checklist: "Checklist website",
      content: "Konten",
      style: "Tampilan",
      advanced: "Lanjutan",
      variant: "Gaya",
      tone: "Latar",
      tones: { default: "Standar", surface: "Lembut", inverted: "Gelap" },
      navLabel: "Nama di menu",
      navLabelHint: "Terpisah dari judul section. Kosongkan untuk memakai nama bawaan.",
      showInNav: "Tampilkan di menu atas",
      anchor: "Anchor (#)",
      anchorHint: "Dipakai untuk tautan ke bagian ini, mis. #menu.",
      anchorTaken: "Anchor sudah dipakai section lain.",
      hidden: "Sembunyikan section ini",
      close: "Tutup panel",
      issues: "Perlu diperbaiki",
      addItem: "Tambah",
      removeItem: "Hapus item",
      itemRemoved: (name: string) => `“${name}” dihapus`,
      moveItemUp: "Naikkan",
      moveItemDown: "Turunkan",
      itemN: (label: string, n: number) => `${label} ${n}`,
      collapse: "Ciutkan",
      expand: "Buka",
      maxItems: (n: number) => `Maksimal ${n} item.`,
      dragItem: "Geser untuk mengurutkan",
      ctaLabel: "Teks tombol",
      ctaKind: "Aksi tombol",
      ctaKinds: { whatsapp: "Chat WhatsApp", link: "Buka tautan" },
      ctaMessage: "Pesan WhatsApp otomatis",
      ctaHref: "Tautan",
      ctaHrefPlaceholder: "https://… atau #kontak",
      ctaNone: "Tanpa tombol",
      ctaAdd: "Tambah tombol",
      ctaRemove: "Hapus tombol",
      placeholders: "Sisipkan:",
      pricePlaceholder: "0",
      tagsPlaceholder: "Ketik lalu Enter",
      fromBusinessInfo: "Isi diambil dari tab Info bisnis.",
      openInfo: "Buka Info bisnis",
    },
    fields: {
      eyebrow: "Label kecil di atas judul",
      title: "Judul",
      description: "Deskripsi",
      body: "Isi teks",
      image: "Gambar",
      primaryCta: "Tombol utama",
      secondaryCta: "Tombol kedua",
      highlights: "Poin unggulan",
      highlight: "Poin",
      items: "Daftar",
      itemTitle: "Nama",
      itemName: "Nama",
      itemDescription: "Keterangan",
      price: "Harga",
      priceNote: "Catatan harga",
      priceNotePlaceholder: "mis. mulai dari, /orang",
      badge: "Label",
      artworkDetails: "Detail karya (opsional)",
      artist: "Seniman / pembuat",
      medium: "Media",
      mediumPlaceholder: "mis. Cat minyak di kanvas",
      size: "Ukuran",
      year: "Tahun",
      status: "Status",
      statusNone: "Tidak ditampilkan",
      statusAvailable: "Tersedia",
      statusReserved: "Dipesan",
      statusSold: "Terjual",
      tags: "Tag",
      categories: "Kategori",
      category: "Kategori",
      categoryName: "Nama kategori",
      menuItems: "Menu",
      menuItem: "Menu",
      orderMessage: "Pesan WhatsApp saat memesan",
      images: "Foto",
      photo: "Foto",
      slide: "Foto slide",
      addSlide: "Tambah foto slide",
      slidesHint: "Foto berganti otomatis di belakang judul. Pakai 2–9 foto mendatar.",
      quote: "Ulasan",
      name: "Nama",
      role: "Keterangan",
      rating: "Rating",
      sample: "Contoh (bukan ulasan asli)",
      sampleWarning: "Ini testimoni contoh. Ganti dengan ulasan asli sebelum publish.",
      question: "Pertanyaan",
      answer: "Jawaban",
      value: "Angka",
      label: "Keterangan",
      note: "Catatan",
      showMap: "Tampilkan peta",
      showWhatsapp: "Tampilkan WhatsApp",
      showPhone: "Tampilkan telepon",
      showEmail: "Tampilkan email",
      showSocial: "Tampilkan media sosial",
      showHours: "Tampilkan jam buka",
      showAddress: "Tampilkan alamat",
      message: "Pesan WhatsApp otomatis",
      plans: "Paket",
      plan: "Paket",
      planName: "Nama paket",
      period: "Periode",
      periodPlaceholder: "/bulan",
      features: "Fitur",
      feature: "Fitur",
      highlighted: "Tandai sebagai unggulan",
      button: "Tombol",
      ctaLabel: "Teks tombol menu",
      showCta: "Tampilkan tombol di menu",
      mobileBar: "Menu bawah di HP (seperti aplikasi)",
      footerText: "Teks footer",
      service: "Layanan",
      product: "Produk",
      testimonial: "Testimoni",
      faq: "Pertanyaan",
      stat: "Angka",
      steps: "Langkah",
      stepLabel: "Penanda (mis. Langkah 1, 2019, 16.00)",
      events: "Acara",
      eventName: "Nama acara",
      date: "Tanggal",
      startTime: "Jam mulai",
      endTime: "Jam selesai",
      timeNote: "Keterangan waktu (mis. WIB, s.d. selesai)",
      venue: "Nama tempat",
      address: "Alamat",
      mapUrl: "Tautan Google Maps",
      showCalendar: "Tampilkan tombol simpan ke kalender",
      zone: "Zona waktu",
      doneText: "Teks setelah hari-H (mis. Hari bahagia telah tiba!)",
      people: "Orang",
      personRole: "Peran / keterangan singkat",
      couple: "Mempelai",
      coupleName: "Nama mempelai",
      coupleRole: "Keterangan singkat (mis. Putri dari)",
      coupleParents: "Nama orang tua",
      instagram: "Instagram (tanpa @)",
      quoteText: "Kutipan",
      cite: "Sumber kutipan",
      accounts: "Rekening & dompet digital",
      provider: "Bank / dompet digital",
      accountNumber: "Nomor",
      accountName: "Atas nama",
      giftAddress: "Alamat kirim kado",
      askAttendance: "Tanyakan kehadiran (Hadir / Tidak hadir)",
      wishesNote: "Ucapan dari tamu muncul di website setelah dipublish. Kelola ucapan di tab Ucapan & RSVP.",
      rsvpDeadline: "Batas konfirmasi",
      postCount: "Jumlah artikel",
      postCountOption: (n: number) => `${n} artikel`,
      postLinkLabel: "Teks tautan ke semua artikel",
      blogLatestNote:
        "Artikel diambil otomatis dari menu Blog di dashboard. Section ini tidak tampil di website selama belum ada artikel yang terbit.",
      maxPax: "Maksimal orang per tamu",
      paxOption: (n: number) => `${n} orang`,
      askNote: "Tampilkan kolom catatan",
      rsvpNote:
        "Tamu yang mengisi lagi dengan nama yang sama memperbarui jawabannya. Lihat hasilnya di tab Ucapan & RSVP.",
      links: "Tautan",
      linkLabel: "Teks tombol",
      linkHighlight: "Sorot tautan ini",
      linkSponsored: "Tautan afiliasi / berbayar",
      url: "Alamat tautan (URL)",
      urlInvalid: "Masukkan tautan yang valid, mis. https://shopee.co.id/…",
      lessonGroups: "Pertemuan / modul",
      lessonGroupLabel: "Penanda (mis. Pertemuan 1, Modul 2)",
      lessonTopics: "Topik materi",
      lessonSummary: "Ringkasan",
      lessonPoints: "Poin penting",
      lessonPoint: "Poin",
      lessonBody: "Isi materi",
      lessonPdf: "Tautan PDF (mis. Google Drive)",
      lessonVideo: "Tautan video YouTube",
      lessonVideoInvalid: "Tempel tautan video YouTube, mis. https://youtu.be/…",
      lessonShowSearch: "Tampilkan kotak cari materi",
      lessonShowFilter: "Tampilkan filter per pertemuan",
      lessonSearchPlaceholder: "Teks di kotak cari",
      lessonPdfLabel: "Teks tombol PDF",
      lessonVideoLabel: "Teks tombol YouTube",
      lessonBackLabel: "Teks tombol kembali",
      lessonNoteTitle: "Judul pengingat di akhir materi",
      lessonNoteBody: "Isi pengingat (mis. Segera ke bidan bila ada tanda bahaya)",
      buttonTexts: "Teks tombol & kotak cari",
      lessonsNote: "Pengunjung mengetuk topik untuk membuka materinya. Setiap topik punya tautan sendiri yang bisa dibagikan.",
      affiliate: "Ini tautan afiliasi (tampilkan keterangan)",
      disclosure: "Keterangan afiliasi",
      pickButton: "Teks tombol (mis. Lihat proyek)",
      store: "Nama toko (otomatis dari tautan bila kosong)",
    },
    image: {
      choose: "Pilih gambar",
      change: "Ganti",
      remove: "Hapus gambar",
      upload: "Unggah foto",
      uploading: (pct: number) => `Mengunggah… ${pct}%`,
      uploadHint: "JPG, PNG, atau WebP. Maksimal 10 MB.",
      tooLarge: "Ukuran foto maksimal 10 MB.",
      badType: "Format harus JPG, PNG, atau WebP.",
      uploaded: "Foto diunggah.",
      library: "Foto Anda",
      stock: "Foto contoh",
      libraryEmpty: "Belum ada foto yang diunggah.",
      alt: "Deskripsi gambar (alt)",
      altHint: "Membantu pengguna tunanetra dan SEO. mis. “Es kopi susu di meja kayu”.",
      altMissing: "Tambahkan deskripsi gambar.",
      focal: "Titik fokus",
      focalHint: "Bagian yang tetap terlihat saat gambar dipotong.",
      focalPoints: [
        "Kiri atas",
        "Atas",
        "Kanan atas",
        "Kiri",
        "Tengah",
        "Kanan",
        "Kiri bawah",
        "Bawah",
        "Kanan bawah",
      ] as const,
      pickerTitle: "Pilih gambar",
      storage: (used: string, limit: string) => `${used} dari ${limit} terpakai`,
      deleteAsset: "Hapus foto",
    },
    ai: {
      rewrite: "Tulis ulang dengan AI",
      tones: { singkat: "Singkat", persuasif: "Persuasif", formal: "Formal", santai: "Santai" },
      toneLabel: "Gaya tulisan",
      generate: "Tulis ulang",
      generating: "AI sedang menulis…",
      queued: (n: number) => `Antrian ke-${n}…`,
      before: "Sebelum",
      after: "Sesudah",
      accept: "Terima",
      reject: "Tolak",
      again: "Coba lagi",
      remaining: (used: number, limit: number) => `Sisa ${Math.max(0, limit - used)} dari ${limit} bulan ini`,
      quotaTitle: "Kuota AI bulan ini habis",
      quotaBody:
        "Upgrade Paket Website untuk kuota tulis ulang lebih banyak, atau tunggu sampai bulan depan.",
      quotaCta: "Lihat paket",
      failed: "AI belum bisa menulis sekarang. Coba lagi sebentar lagi.",
      fallback: "AI sedang sibuk. Coba lagi dalam beberapa menit.",
      emptyText: "Isi teksnya dulu, lalu minta AI menulis ulang.",
      pickField: "Pilih teks yang ingin ditulis ulang:",
      accepted: "Teks diperbarui.",
    },
    theme: {
      title: "Tema",
      subtitle: "Ganti tema kapan saja — konten Anda tidak berubah.",
      current: "Tema aktif",
      applied: (name: string) => `Tema ${name} diterapkan.`,
      primary: "Warna utama",
      primaryHint: "Dipakai untuk tombol dan aksen.",
      hex: "Kode warna",
      fontPair: "Pasangan huruf",
      fontSample: "Kopi enak, harga bersahabat.",
      resetOverrides: "Kembalikan ke bawaan tema",
      contrastWarning: "Perhatian kontras",
      premiumNote: "Tema premium bisa dicoba gratis. Bayar saat publish.",
      default: "Bawaan tema",
    },
    info: {
      title: "Info bisnis",
      titleInvitation: "Info undangan",
      titleEvent: "Info acara",
      subtitle: "Dipakai di banyak section sekaligus: kontak, lokasi, jam buka, footer.",
      subtitleEvent: "Dipakai di banyak section sekaligus: kontak, lokasi, footer.",
      identity: "Identitas",
      name: "Nama usaha",
      tagline: "Tagline",
      logo: "Logo",
      logoHint: "Muncul di menu website, di samping nama usaha.",
      logoUploaded: "Logo ditambahkan.",
      contact: "Kontak",
      whatsapp: "WhatsApp",
      phone: "Telepon",
      email: "Email",
      location: "Lokasi",
      address: "Alamat",
      city: "Kota",
      mapsUrl: "Tautan Google Maps",
      mapsUrlHint: "Buka Google Maps → Bagikan → Salin link.",
      geo: "Koordinat (opsional)",
      lat: "Lintang",
      lng: "Bujur",
      social: "Media sosial",
      socialNetworks: { instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook", youtube: "YouTube" },
      socialPlaceholder: "namaakun",
      hours: "Jam buka",
      hoursEmpty: "Belum diatur. Tambahkan agar pelanggan tahu kapan Anda buka.",
      hoursAdd: "Atur jam buka",
      closed: "Tutup",
      open: "Buka",
      openTime: "Jam buka",
      closeTime: "Jam tutup",
      copyToAll: "Salin ke semua hari",
      days: {
        mon: "Senin",
        tue: "Selasa",
        wed: "Rabu",
        thu: "Kamis",
        fri: "Jumat",
        sat: "Sabtu",
        sun: "Minggu",
      },
      waMessage: "Pesan WhatsApp bawaan",
      waMessageHint: "Pesan yang otomatis terisi saat pelanggan menekan tombol WhatsApp.",
      placeholderName: "nama usaha",
      placeholderProduct: "produk",
    },
    seo: {
      title: "SEO",
      subtitle: "Atur bagaimana halaman ini tampil di Google.",
      pageTitle: "Judul di Google",
      pageDescription: "Deskripsi di Google",
      preview: "Pratinjau di Google",
      noDescription: "Belum ada deskripsi",
      autoHint:
        "Kolom yang kosong diisi otomatis dari nama usaha, tagline, dan isi halaman — seperti pratinjau di atas.",
      auto: "Buat SEO otomatis",
      autoRunning: "AI sedang menyusun…",
      autoDone: "SEO diperbarui oleh AI.",
      noindex: "Sembunyikan halaman ini dari Google",
      ogImage: "Gambar saat dibagikan",
      ogImageHint: "Muncul saat tautan dibagikan di WhatsApp atau media sosial. Ideal 1200×630.",
      pageTitleLabel: "Nama halaman",
      pageSlug: "Alamat halaman",
      tooLong: "Terlalu panjang — Google akan memotongnya.",
      tooShort: "Sebaiknya lebih panjang agar informatif.",
    },
    publishDialog: {
      title: "Publish website",
      checking: "Memeriksa website…",
      saving: "Menyimpan perubahan terakhir…",
      ready: "Siap dipublish!",
      readyBody: "Semua perubahan akan langsung terlihat pengunjung.",
      blockers: "Harus diperbaiki dulu",
      warnings: "Sebaiknya diperbaiki",
      warningsHint: "Anda tetap bisa publish.",
      fix: "Perbaiki",
      fixWhatsapp: "Isi nomor",
      republish: "Publish ulang",
      publishNow: "Publish sekarang",
      publishing: "Mempublish…",
      publishAnyway: "Tetap publish",
      successTitle: "Website Anda sudah live! 🎉",
      successBody: "Bagikan tautan ini ke pelanggan Anda.",
      unchanged: "Tidak ada perubahan untuk dipublish",
      unchangedBody: "Website live sudah sama dengan draft Anda.",
      share: "Bagikan ke WhatsApp",
      shareMessage: (name: string, url: string) => `Halo! Kunjungi website ${name} di ${url}`,
      connectDomain: "Hubungkan domain",
      viewSite: "Lihat website",
      domainAfter:
        "Subdomain langsung aktif setelah publish. Domain kustom baru bisa dibuka setelah DNS terhubung.",
      checkFailed: "Pemeriksaan gagal. Coba lagi.",
      saveFailed: "Perubahan terakhir belum tersimpan. Periksa koneksi lalu coba lagi.",
      paywallTitle: "Tema premium ini perlu dibuka dulu",
      paywallTemplateTitle: "Template premium ini perlu dibuka dulu",
      paywallBody: (price: string) =>
        `Sekali bayar ${price} untuk website ini. Anda tetap bisa mengedit dan melihat pratinjau sepuasnya.`,
      paywallCta: "Tambah ke keranjang & bayar",
      premiumTitle: "Buka item premium untuk publish",
      premiumCta: "Buka sekarang",
      premiumCtaPromo: "Buka dengan voucher",
      premiumOnce: "Sekali bayar untuk semuanya, berlaku selamanya.",
      switchFree: "Ganti ke tema gratis",
    },
    conflict: {
      title: "Website ini diubah di tab/perangkat lain",
      body: "Ada versi yang lebih baru dari yang Anda edit. Pilih versi mana yang ingin dipakai.",
      reload: "Muat versi terbaru",
      reloadHint: "Perubahan Anda sejak simpan terakhir akan dibuang.",
      overwrite: "Timpa dengan perubahan saya",
      overwriteHint: "Versi dari tab/perangkat lain akan diganti.",
    },
    shortcutsDialog: {
      title: "Pintasan keyboard",
      items: [
        ["⌘/Ctrl + Z", "Urungkan"],
        ["⇧ + ⌘ + Z / Ctrl + Y", "Ulangi"],
        ["⌘/Ctrl + S", "Simpan sekarang"],
        ["Esc", "Batal pilih / tutup panel"],
        ["Delete / Backspace", "Hapus section terpilih"],
        ["Alt + ↑ / ↓", "Pindahkan section"],
        ["?", "Tampilkan pintasan"],
      ] as const,
    },
    coach: {
      steps: [
        {
          title: "Klik untuk mengedit",
          body: "Klik bagian mana pun di pratinjau untuk mengubah teks, foto, dan tombol.",
        },
        {
          title: "Atur susunan halaman",
          body: "Tambah, sembunyikan, atau geser section dari panel kiri. Ganti tema di tab Tema.",
        },
        {
          title: "Publish saat siap",
          body: "Perubahan tersimpan otomatis. Tekan Publish agar website bisa dikunjungi.",
        },
      ] as const,
      next: "Lanjut",
      done: "Mulai mengedit",
      skip: "Lewati",
      progress: (n: number, total: number) => `${n}/${total}`,
    },
    offlineRestored: "Perubahan yang tersimpan di perangkat ini dipulihkan.",
    loadFailed: "Editor gagal dimuat",
    invalidDraft: "Draft website ini tidak valid. Hubungi bantuan agar kami bisa memulihkannya.",
    checklist: {
      whatsapp: "Nomor WhatsApp terisi",
      testimonials: "Testimoni asli (bukan contoh)",
      images: "Semua gambar punya deskripsi",
      seo: "Judul & deskripsi SEO terisi",
    },
    deleteSelectedHint: "Section dihapus. Tekan Urungkan untuk mengembalikan.",
  },
  preview: {
    title: "Pratinjau draft",
    back: "Kembali ke editor",
    draftBadge: "Draft — belum dipublish",
    device: "Ukuran layar",
  },
  dev: {
    catalogTitle: "Galeri katalog (dev)",
    catalogSubtitle: "Setiap tema × template di 390 / 768 / 1280 px. Edit file .md untuk hot reload.",
    errors: "Error",
    warnings: "Peringatan",
    noErrors: "Tidak ada error.",
    compileFailed: "Katalog gagal dikompilasi.",
    previewMissing: "Template atau tema tidak ditemukan. Kembali ke",
  },
  admin: {
    title: "Admin",
    menu: "Menu admin",
    back: "Ke aplikasi",
    forbidden: "Halaman ini khusus admin.",
    search: "Cari",
    searchPlaceholder: "Cari…",
    reason: "Alasan",
    reasonRequired: "Tuliskan alasan.",
    save: "Simpan",
    saved: "Tersimpan.",
    empty: "Belum ada data.",
    collapse: "Ciutkan menu",
    expand: "Bentangkan menu",
    reset: "Reset",
    actions: "Aksi",
    edit: "Ubah",
    view: "Detail",
    add: "Tambah",
    prev: "Sebelumnya",
    next: "Berikutnya",
    pageLabel: "Halaman {page}",
    range: "{from}–{to} dari {total}",
    allStatuses: "Semua status",
    allKinds: "Semua jenis",
    show: "Tampilkan",
    hide: "Sembunyikan",
    enable: "Aktifkan",
    disable: "Nonaktifkan",
    check: "Periksa",
    slugPlaceholder: "slug",
    keyPlaceholder: "kunci fitur",
    messagePlaceholder: "Tulis pengumuman",
    groups: { ops: "Operasi", catalog: "Katalog", system: "Sistem" },
    nav: {
      stats: "Dashboard",
      users: "Pengguna",
      sites: "Website",
      orders: "Pesanan",
      reports: "Laporan",
      catalog: "Katalog",
      plans: "Paket",
      tlds: "Domain",
      settings: "Pengaturan",
      jobs: "AI",
      announcements: "Pengumuman",
      vouchers: "Voucher",
      flags: "Fitur",
      blocklist: "Blokir",
      audit: "Audit",
      domains: "Hostname",
      verify: "Verifikasi Transfer",
    },
    vouchers: {
      create: "Buat voucher",
      code: "Kode",
      codeHint: "Huruf besar, angka, atau tanda hubung. Pembeli mengetik kode ini saat checkout.",
      codeLocked: "Kode tidak bisa diubah setelah dibuat.",
      description: "Keterangan",
      discount: "Diskon",
      used: "Terpakai",
      period: "Periode",
      always: "Selalu",
      active: "Aktif",
      inactive: "Nonaktif",
      full: "Kuota habis",
      ended: "Berakhir",
      type: "Jenis diskon",
      percent: "Persen (%)",
      fixed: "Potongan rupiah",
      valuePercent: "Persentase (1–100)",
      valueFixed: "Potongan (Rp)",
      appliesTo: "Berlaku untuk",
      appliesToHint: "Tidak memilih apa pun berarti berlaku untuk semua item.",
      kinds: {
        template: "Template",
        design: "Tema",
        site_plan: "Paket website",
        domain_register: "Domain baru",
        domain_renew: "Perpanjang domain",
      },
      quota: "Kuota total",
      quotaHint: "Jumlah pemakaian untuk semua pengguna. Kosongkan untuk tanpa batas.",
      perUser: "Batas per pengguna",
      startsAt: "Mulai",
      endsAt: "Berakhir",
      activeLabel: "Voucher aktif",
      featuredLabel: "Tampilkan sebagai promo",
      featuredHint:
        "Diiklankan di aplikasi (pembuatan website, ringkasan website, checkout) dan bisa dipakai sekali klik.",
      featuredBadge: "Promo",
      planSection: "Promo paket website",
      planSectionHint:
        "Berlaku untuk item paket website. Link promo membuat pembeli bisa klaim paket tanpa membuat website dulu.",
      plans: "Hanya untuk paket",
      plansHint: "Kosongkan untuk semua paket.",
      maxYears: "Tahun yang didiskon",
      maxYearsHint: "Isi 1 untuk diskon tahun pertama saja. Kosongkan untuk semua tahun.",
      bonusTld: "Bonus domain gratis",
      bonusTldHint: "Contoh: my.id. Pembeli lewat link promo dapat 1 domain ini gratis tahun pertama.",
      promoLink: "Link promo",
      promoLinkHint: "Bagikan link ini: pengunjung langsung klaim, daftar, dan bayar.",
      copyLink: "Salin link promo",
      linkCopied: "Link promo disalin.",
      periods: { 7: "7 hari", 30: "30 hari", 90: "90 hari" },
      periodLabel: "Periode laporan",
      kpi: {
        discount: "Diskon diberikan",
        discountHint: (n: number) => `${n} pemakaian lunas`,
        redemptions: "Pemakaian lunas",
        redemptionsHint: (free: number) => `${free} pesanan digratiskan`,
        revenue: "Pendapatan setelah diskon",
        revenueHint: (gross: string) => `dari nilai ${gross} sebelum diskon`,
        users: "Pengguna unik",
        usersHint: (all: number) => `${all} sepanjang waktu`,
      },
      pipeline: {
        title: "Status pemakaian",
        paid: "Lunas (diskon terpakai)",
        reserved: "Menunggu pembayaran (kuota tertahan)",
        released: "Batal / kedaluwarsa (kuota kembali)",
        reservedNote: (amount: string) => `Diskon tertahan ${amount}`,
      },
      trend: {
        redemptions: "Pemakaian per hari",
        discount: "Diskon per hari",
        redemptionsLabel: "Grafik pemakaian voucher per hari",
        discountLabel: "Grafik nilai diskon per hari",
        series: "Pemakaian",
        seriesDiscount: "Diskon",
      },
      perVoucher: "Performa tiap voucher",
      cols: {
        quota: "Kuota",
        paid: "Lunas",
        pending: "Tertahan",
        freed: "Kembali",
        given: "Diskon",
        revenue: "Pendapatan",
        lastUsed: "Terakhir dipakai",
      },
      never: "Belum pernah",
      unlimited: "tanpa batas",
      log: {
        title: "Riwayat pemakaian",
        empty: "Belum ada yang memakai voucher.",
        all: "Semua voucher",
        filter: "Filter voucher",
        user: "Pengguna",
        order: "Pesanan",
        site: "Website",
        when: "Waktu",
        discount: "Diskon",
        paidTotal: "Dibayar",
        more: "Muat lebih banyak",
        states: { paid: "Lunas", reserved: "Menunggu bayar", released: "Dibatalkan" },
      },
      viewLog: "Lihat riwayat",
      viewPromoStats: "Statistik promo",
      promoStats: {
        title: (code: string) => `Statistik promo ${code}`,
        lead: "Seberapa efektif link promo ini: dari kunjungan halaman sampai paket dipakai dan domain diklaim.",
        back: "Semua voucher",
        openPage: "Buka halaman promo",
        status: {
          active: "Aktif",
          inactive: "Nonaktif",
          ended: "Berakhir",
          scheduled: "Terjadwal",
          full: "Kuota habis",
        },
        endsIn: (d: number) => (d <= 0 ? "Berakhir hari ini" : `Berakhir ${d} hari lagi`),
        endedOn: (date: string) => `Berakhir ${date}`,
        noDeadline: "Tanpa batas waktu",
        kpi: {
          visitors: "Pengunjung",
          visitorsHint: (n: number) => `${n} sudah masuk akun`,
          conversion: "Konversi",
          conversionHint: (paid: number) => `${paid} pembelian lunas`,
          revenue: "Pendapatan",
          revenueHint: (total: string, aov: string) => `${total} · rata-rata ${aov}`,
          quota: "Kuota terpakai",
          quotaHint: (left: number) => `Sisa ${left} slot`,
          quotaUnlimited: "Tanpa batas kuota",
        },
        funnel: {
          title: "Corong konversi",
          note: "Persentase dari langkah sebelumnya",
          steps: {
            visitors: "Kunjungi halaman",
            checkout: "Mulai checkout",
            paid: "Bayar lunas",
            planApplied: "Paket dipakai di website",
            domainClaimed: "Domain bonus diklaim",
          },
          pending: (n: number) => `${n} pesanan menunggu bayar`,
          dropOff: (n: number) => `${n} berhenti di sini`,
        },
        trend: {
          title: "Tren harian",
          visitors: "Pengunjung",
          paid: "Lunas",
          label: "Pengunjung dan pembelian lunas per hari",
        },
        sources: {
          title: "Sumber pengunjung",
          direct: "Langsung / tanpa sumber",
          empty: "Belum ada data sumber.",
          tip: "Tambahkan ?utm_source=instagram di akhir link untuk melacak tiap kanal.",
        },
        devices: { title: "Perangkat", mobile: "HP", tablet: "Tablet", desktop: "Desktop" },
        emptyTitle: "Belum ada pengunjung",
        emptyBody:
          "Bagikan link promo ini di WhatsApp, Instagram, atau iklan. Angka akan muncul di sini setelah ada yang membuka halaman.",
        buyers: "Pembeli terbaru",
        privacy:
          "Pengunjung dihitung sekali per hari tanpa cookie. Bot dan pratinjau link (WhatsApp, Facebook) tidak dihitung.",
        notFound: "Voucher tidak ditemukan.",
      },
    },
    lead: {
      stats: "Pendapatan, website, domain, dan kesehatan AI.",
      users: "Cari akun, tangguhkan, atau ubah peran.",
      sites: "Website pelanggan dan status tayangnya.",
      orders: "Pembayaran dan pemenuhan pesanan.",
      verify: "Periksa bukti transfer bank dari pelanggan, lalu setujui atau tolak.",
      vouchers: "Kode diskon dengan kuota, misalnya template gratis untuk 100 pengguna pertama.",
      reports: "Laporan penyalahgunaan dari pengunjung.",
      catalog: "Harga, urutan, unggulan, dan visibilitas desain serta template di wizard.",
      plans: "Harga, fitur, dan batas paket website.",
      tlds: "Markup dan ketersediaan akhiran domain.",
      domains: "Hostname yang mengarah ke website.",
      settings: "Profil perusahaan dan metode pembayaran.",
      jobs: "Antrean generasi dan penulisan AI.",
      announcements: "Pesan di bagian atas aplikasi.",
      flags: "Sakelar fitur untuk sebagian pengguna.",
      blocklist: "Slug yang tidak boleh dipakai pelanggan.",
      audit: "Jejak perubahan yang dilakukan admin.",
    },
    stats: {
      users: "Pengguna",
      sites: "Website",
      published: "Tayang",
      sitesLive: "Website tayang",
      sitesOfTotal: (total: string, drafts: string) => `dari ${total} dibuat · ${drafts} belum tayang`,
      revenue: "Pendapatan 30 hari",
      reports: "Laporan terbuka",
      ai: "Job AI 7 hari",
      last7d: "7 hari terakhir",
      paid: "Lunas 30 hari",
      attention: "Perlu perhatian",
      pendingDomains: "Domain menunggu",
      activeDomains: "Domain aktif",
      failedDomains: "Domain gagal",
      queue: "Antrean",
      model: "Model",
      ready: "Siap",
      notReady: "Tidak siap",
      failure: "Gagal",
      fallback: "Fallback",
      registrar: "Registrar",
      latency: "Latensi",
      websites: "Website",
      domains: "Hostname",
      aiHealth: "Kesehatan AI",
      attentionTitle: "Perlu tindakan",
      recentOrders: "Pesanan terbaru",
      viewAll: "Lihat semua",
      notLive: "Belum tayang",
      newSites: "Website baru",
      newUsers: "Pengguna baru",
      success: "Berhasil",
      calm: "Tidak ada yang perlu ditindak sekarang.",
      openReports: "Laporan terbuka",
      manualPending: "Bukti transfer menunggu verifikasi",
      lowBalance: "Saldo registrar rendah",
      payments: "Pembayaran",
      security: "Keamanan",
      activeAdmins: "Admin aktif",
      signup: "Pendaftaran",
      signupOpen: "Pendaftaran dibuka",
      signupClosed: "Pendaftaran ditutup",
      sessionRevoke: "Menangguhkan akun langsung mencabut sesi masuknya.",
      payOff: "Tidak dipakai di checkout",
      payDummy: "Simulasi. Tidak menarik uang sungguhan.",
      paySandbox: "Masih mengarah ke sandbox",
      paySnapSandbox: "Snap sandbox",
      payMissing: "Kunci belum lengkap",
      payReady: "Siap dipakai",
      paySim: "Checkout memakai simulasi",
      paySandboxOn: "Pembayaran sandbox aktif",
      payKeys: "Kunci pembayaran belum lengkap",
      oneAdmin: "Hanya satu admin aktif",
      trendsTitle: "Tren 30 hari",
      revenueChart: "Pendapatan harian (30 hari)",
      revenueChartLabel: "Grafik pendapatan harian 30 hari terakhir",
      growthChart: "Pendaftar & website baru",
      growthChartLabel: "Grafik pengguna dan website baru per hari, 30 hari terakhir",
      aiChart: "Hasil job AI (30 hari)",
      aiChartLabel: "Grafik hasil job AI per hari: berhasil, fallback template, dan gagal",
      aiFallbackHigh: "Banyak job AI memakai fallback",
      aiFailedJobs: "Job AI gagal (7 hari)",
      ordersPaid: "Pesanan lunas",
      noTrend: "Belum ada data 30 hari terakhir.",
    },
    jobs: {
      outcome: "Hasil",
      allOutcomes: "Semua hasil",
      outcomes: {
        ok: "Berhasil (AI)",
        fallback: "Fallback template",
        failed: "Gagal",
        running: "Berjalan",
        queued: "Antrean",
      },
      outcomeHint: {
        ok: "Konten ditulis oleh AI.",
        fallback: "AI tidak dipakai, konten diisi dari template.",
        failed: "Tidak ada hasil yang disimpan.",
        running: "Sedang diproses.",
        queued: "Menunggu giliran.",
      },
      reasons: {
        disabled: "AI dimatikan",
        timeout: "AI terlalu lama",
        unavailable: "AI tidak bisa dihubungi",
        invalid_output: "Jawaban AI tidak valid",
        busy: "Antrean penuh (konten awal dipakai)",
        skipped: "Dilewati pengguna",
        unknown: "Alasan tidak tercatat",
      },
      summaryOk: "Berhasil (AI)",
      summaryFallback: "Fallback template",
      summaryFailed: "Gagal",
      summaryLatency: "Latensi median",
      of7d: (n: number) => `dari ${n} job, 7 hari`,
      reasonsTitle: "Kenapa fallback? (7 hari)",
      noReasons: "Tidak ada fallback 7 hari terakhir.",
      when: "Waktu",
      site: "Website",
      duration: "Durasi",
      attempts: "Percobaan",
      detail: "Detail",
      detailTitle: "Detail job AI",
      error: "Pesan error",
      noError: "Tidak ada pesan error.",
      template: "Template",
      provider: "Penyedia",
      promptVersion: "Versi prompt",
      created: "Dibuat",
      started: "Mulai",
      finished: "Selesai",
      jobId: "ID job",
    },
    status: {
      active: "Aktif",
      suspended: "Ditangguhkan",
      admin: "Admin",
      user: "Pengguna",
      draft: "Draft",
      published: "Live",
      paid: "Lunas",
      pending: "Menunggu",
      failed: "Gagal",
      expired: "Kedaluwarsa",
      cancelled: "Dibatalkan",
      refunded: "Dikembalikan",
      queued: "Antrean",
      running: "Berjalan",
      done: "Selesai",
      open: "Terbuka",
      dismissed: "Diabaikan",
      actioned: "Ditindak",
      hidden: "Disembunyikan",
      deprecated: "Usang",
      design: "Desain",
      template: "Template",
      subdomain: "Subdomain",
      custom: "Kustom",
      purchased: "Dibeli",
      verifying: "Dicek",
      registering: "Didaftarkan",
      inactive: "Nonaktif",
    },
    cols: {
      user: "Pengguna",
      role: "Peran",
      lastLogin: "Masuk terakhir",
      status: "Status",
      sites: "Website",
      action: "Aksi",
      owner: "Pemilik",
      number: "Nomor",
      total: "Total",
      price: "Harga",
      kind: "Jenis",
      host: "Hostname",
      when: "Waktu",
      actor: "Pelaku",
      name: "Nama",
      plan: "Paket",
      created: "Dibuat",
    },
    users: {
      suspend: "Tangguhkan",
      activate: "Aktifkan",
      makeAdmin: "Jadikan admin",
      makeUser: "Jadikan pengguna",
      neverLogin: "Belum pernah masuk",
      managePlans: "Ubah paket website",
      whatsapp: "WhatsApp",
      noWhatsapp: "Belum ada nomor",
      chatWhatsapp: (name: string) => `Chat ${name} di WhatsApp`,
      whatsappSource: {
        signup: "dari pendaftaran",
        site: "dari website",
        domain: "dari kontak domain",
      } as Record<string, string>,
      whatsappGreeting: (name: string) =>
        `Halo ${name}, saya dari tim BazeBuild. Terima kasih sudah daftar! Ada yang bisa kami bantu untuk membuat website Anda?`,
    },
    sites: {
      suspend: "Tangguhkan",
      unsuspend: "Pulihkan",
      plan: "Paket",
      changePlan: "Ubah paket (gratis)",
      until: (d: string) => `s.d. ${d}`,
      forever: "Tanpa batas waktu",
    },
    planGrant: {
      title: (site: string) => `Ubah paket ${site}`,
      description:
        "Pemilik tidak ditagih. Paket langsung aktif dan menggantikan paket berbayar yang sedang berjalan.",
      current: "Paket saat ini",
      choose: "Paket baru",
      perYear: (price: string) => `${price}/tahun`,
      free: "Gratis",
      duration: "Durasi",
      months: (n: number) => (n % 12 === 0 ? `${n / 12} tahun` : `${n} bulan`),
      until: (d: string) => `Aktif sampai ${d}`,
      downgrade: "Website kembali ke paket gratis. Paket berbayar yang sedang aktif langsung berakhir.",
      reason: "Alasan",
      reasonPlaceholder: "Contoh: kompensasi gangguan, mitra, uji coba",
      reasonQuick: ["Bonus promosi", "Kompensasi gangguan", "Mitra / partner", "Pembayaran di luar sistem"],
      submit: "Terapkan paket",
      same: "Pilih paket yang berbeda atau ubah durasinya.",
      done: (plan: string) => `Paket ${plan} diterapkan.`,
    },
    orders: {
      retry: "Ulangi pemenuhan",
      review: "Tinjau bukti transfer",
      cancel: "Batalkan pesanan",
      cancelTitle: "Batalkan pesanan ini?",
      cancelBody: (n: string) =>
        `Pesanan ${n} dibatalkan dan bukti transfer yang menunggu ditutup. Pelanggan perlu checkout ulang.`,
      cancelled: "Pesanan dibatalkan.",
      manual: "Transfer manual",
      detail: "Lihat detail",
      attention: "Perlu penanganan",
      detailTitle: (n: string) => `Pesanan ${n}`,
      detailDescription: "Isi pesanan, kegagalan pemenuhan, dan riwayat pembayaran.",
      info: "Ringkasan",
      customer: "Pelanggan",
      provider: "Metode bayar",
      created: "Dibuat",
      paidAt: "Dibayar",
      items: "Isi pesanan & status",
      error: "Penyebab gagal",
      copyError: "Salin pesan error",
      retryHint:
        "Sudah memperbaiki penyebabnya? Jalankan ulang item yang gagal. Pelanggan tidak ditagih lagi.",
      retried: "Pemenuhan dijalankan ulang.",
      history: "Riwayat",
      historyEmpty: "Belum ada riwayat.",
      eventLabels: {
        proof_submitted: "Bukti transfer dikirim",
        proof_rejected: "Bukti transfer ditolak",
        paid: "Pembayaran diterima",
        fulfilled: "Item berhasil diproses",
        fulfill_failed: "Item gagal diproses",
        cancelled_by_admin: "Dibatalkan admin",
      } as Record<string, string>,
    },
    proofs: {
      tabs: { submitted: "Menunggu", approved: "Disetujui", rejected: "Ditolak" },
      emptySubmitted: "Tidak ada bukti yang menunggu. Semua beres!",
      emptyOther: "Belum ada data.",
      order: "Pesanan",
      customer: "Pelanggan",
      sender: "Pengirim",
      claimed: "Nominal transfer",
      bill: "Tagihan",
      sentAt: "Dikirim",
      transferredAt: "Tanggal transfer",
      mismatch: "Nominal berbeda",
      match: "Nominal sesuai",
      review: "Tinjau",
      reviewTitle: (n: string) => `Bukti transfer ${n}`,
      reviewDescription: "Cocokkan dengan mutasi rekening sebelum menyetujui.",
      openFile: "Buka di tab baru",
      fileLoading: "Memuat bukti…",
      fileFailed: "File bukti tidak bisa dimuat.",
      payment: "Detail transfer",
      items: "Isi pesanan",
      destination: "Rekening tujuan",
      note: "Catatan pelanggan",
      orderStatus: "Status pesanan",
      history: "Riwayat bukti pesanan ini",
      approve: "Setujui pembayaran",
      approveTitle: "Setujui pembayaran ini?",
      approveBody: (amount: string, n: string) =>
        `Pastikan ${amount} sudah masuk ke rekening. Pesanan ${n} akan ditandai lunas dan langsung diproses. Tindakan ini tidak bisa dibatalkan.`,
      approved: "Pembayaran disetujui. Pesanan sedang diproses.",
      reject: "Tolak",
      rejectTitle: "Tolak bukti transfer?",
      rejectBody:
        "Pelanggan menerima alasan ini lewat email dan bisa mengunggah bukti baru. Pesanan tetap menunggu pembayaran.",
      rejectReason: "Alasan penolakan",
      rejectPlaceholder: "Jelaskan apa yang perlu diperbaiki pelanggan",
      rejectQuick: [
        "Nominal tidak sesuai tagihan",
        "Bukti tidak terbaca / buram",
        "Dana belum masuk ke rekening kami",
        "Rekening tujuan tidak sesuai",
      ],
      rejected: "Bukti ditolak. Pelanggan sudah diberi tahu.",
      reviewedAt: (d: string) => `Ditinjau ${d}`,
      orderNotPending: "Pesanan ini sudah tidak menunggu pembayaran.",
    },
    reports: {
      dismiss: "Abaikan",
      action: "Tindak",
      detail: "Detail laporan",
      reporter: "Pelapor",
      details: "Keterangan",
      noDetails: "Tidak ada keterangan tambahan.",
      noReporter: "Pengirim tidak meninggalkan email.",
    },
    annForm: {
      add: "Pengumuman baru",
      edit: "Ubah pengumuman",
      message: "Pesan",
      level: "Tingkat",
      levels: { info: "Info", warning: "Peringatan", critical: "Penting" },
      audience: "Yang melihat",
      audiences: { all: "Semua pengguna", free: "Paket gratis", paid: "Paket berbayar", admin: "Admin saja" },
      dismissible: "Bisa ditutup pengguna",
      startsAt: "Mulai tampil",
      endsAt: "Berhenti tampil",
      endsHint: "Kosongkan jika tanpa batas.",
    },
    blockForm: {
      add: "Blokir baru",
      kind: "Jenis",
      kinds: { slug: "Slug website", domain: "Domain", keyword: "Kata", email_domain: "Domain email" },
      value: "Nilai",
      valueHint: "Disimpan dalam huruf kecil. Contoh: admin, casino.com, mail.ru",
    },
    auditDetail: {
      title: "Detail jejak",
      target: "Target",
      meta: "Data tambahan",
      noMeta: "Tidak ada data tambahan.",
      noReason: "Tanpa alasan.",
    },
    removeTitle: "Hapus ini?",
    removeBody: "Data yang dihapus tidak bisa dikembalikan.",
    catalogPage: {
      note: "Tema dan template bisa ditambah dan diubah di sini. Menyembunyikan atau menandai usang mengeluarkan item dari wizard.",
      groupDesignHint: "Warna, font, dan suara tema. Tambah atau ubah dari sini.",
      groupTemplateHint:
        "Susunan halaman untuk tiap jenis usaha. Mulai dari template yang sudah ada, lalu sesuaikan.",
      sectionIdentity: "Identitas",
      sectionLook: "Tampilan",
      sectionVoice: "Suara",
      addDesign: "Desain baru",
      editDesign: "Ubah desain",
      designSaved: "Desain tersimpan.",
      designId: "Id",
      designIdHint: "Huruf kecil dan tanda hubung. Tidak bisa diubah setelah dibuat.",
      designName: "Nama",
      designDescription: "Deskripsi",
      designIndustries: "Jenis usaha",
      designFont: "Pasangan font",
      designColors: "Warna",
      designVoice: "Suara",
      designVoiceHint:
        "Gaya bahasa AI saat menulis website dengan tema ini. Bahasa Indonesia, paling panjang 600 karakter.",
      designVariants: "Varian section",
      designSpacing: "Jarak",
      designMotion: "Animasi",
      designMotionHint: "Gaya gerak di website live (tidak tampil di editor).",
      designTexture: "Tekstur",
      designTextureHint:
        "Lapisan halus di seluruh halaman: grain, garis kisi, titik, kertas, cetak koran, atau aurora dan langit malam (untuk tema gelap).",
      designAmbient: "Partikel sampul",
      designAmbientHint:
        "Yang melayang di sampul undangan: kelopak, salju, bintang, kunang-kunang, atau gelembung.",
      designHeadingWeight: "Ketebalan judul",
      designHeadingCase: "Bentuk judul",
      designIdTaken: "Id tema sudah dipakai.",
      designRequired: "Lengkapi nama, deskripsi, jenis usaha, dan suara.",
      preview: "Pratinjau",
      previewSample: "Kedai Contoh · Bandung",
      previewEmpty: "Lengkapi desain yang cocok untuk melihat pratinjau.",
      previewSection: "Lihat bagian ini di pratinjau",
      addTemplate: "Template baru",
      editTemplate: "Ubah template",
      templateSaved: "Template tersimpan.",
      templatePick: "Pilih titik mulai",
      templatePickHint:
        "Template baru berangkat dari yang sudah ada, supaya isi halaman tetap utuh. Warna mengikuti desain.",
      templateDesigns: "Desain yang cocok",
      templateDesignsHint: "Desain ini yang disarankan saat orang memilih template.",
      templatePages: "Halaman",
      templateAddSection: "Tambah bagian",
      templateAddPage: "Halaman baru",
      templatePageTitle: "Judul halaman",
      templateSlug: "Slug",
      templateRequired: "Lengkapi nama, deskripsi, dan minimal satu desain.",
      templateId: "Id",
      colors: {
        primary: "Utama",
        onPrimary: "Teks di utama",
        secondary: "Sekunder",
        accent: "Aksen",
        background: "Latar",
        surface: "Permukaan",
        text: "Teks",
        muted: "Teks redup",
        border: "Garis",
        inverted: "Terbalik",
        onInverted: "Teks di terbalik",
      },
      search: "Cari nama atau id",
      all: "Semua",
      templates: "Template",
      designs: "Desain",
      statusAll: "Semua status",
      featured: "Unggulan",
      featuredHint: "Ditandai agar lebih mudah dipilih di wizard.",
      hiddenCount: "Disembunyikan",
      sort: "Urutan",
      sortHint: "Angka lebih kecil tampil lebih dulu.",
      price: "Harga sekali bayar",
      priceHint: "Harga awal dari katalog: {price}.",
      resetPrice: "Pakai harga awal",
      adjust: "Atur",
      moveUp: "Naikkan",
      moveDown: "Turunkan",
      noMatch: "Tidak ada item yang cocok.",
      saved: "Perubahan katalog tersimpan.",
      statusLabel: "Status di wizard",
      file: "Berkas",
      version: "Versi {version}",
      invalidNumber: "Isi angka bulat.",
      priceNegative: "Harga tidak boleh negatif.",
    },
    settings: {
      signup: "Pendaftaran dibuka",
      aiEnabled: "AI aktif",
      aiEnabledHint:
        "Matikan agar website baru langsung jadi dari contoh isi template, tanpa menunggu AI. Tulisan dan tombol AI disembunyikan dari pelanggan.",
      maintenance: "Pesan pemeliharaan",
      company: "Nama perusahaan",
      whatsapp: "WhatsApp",
      email: "Email",
      profileTitle: "Profil perusahaan",
      profileHint:
        "Ini yang dilihat pengunjung di situs BazeBuild, footer, halaman kontak, dan tombol bantuan.",
      identity: "Identitas",
      contact: "Kontak",
      place: "Alamat dan jam",
      social: "Media sosial",
      socialHint: "Nama akun atau tautan https. Kosongkan yang tidak dipakai.",
      phone: "Telepon",
      address: "Alamat",
      hours: "Jam operasional",
      hoursHint: "Contoh: Senin–Jumat, 09.00–18.00 WIB",
      whatsappHint: "Angka saja, diawali 62. Dipakai tombol bantuan dan situs publik.",
      whatsappMessage: "Pesan WhatsApp awal",
      whatsappMessageHint: "Teks yang sudah terisi saat pengunjung membuka chat.",
      preview: "Pratinjau publik",
      previewEmpty: "Belum ada kontak. Isi WhatsApp, email, atau alamat.",
      operations: "Operasi",
      operationsHint: "AI, pendaftaran akun baru, dan pesan saat aplikasi sedang dipelihara.",
      companyRequired: "Nama perusahaan wajib diisi.",
      whatsappInvalid: "WhatsApp harus angka dan diawali 62.",
      instagram: "Instagram",
      tiktok: "TikTok",
      facebook: "Facebook",
      youtube: "YouTube",
      linkedin: "LinkedIn",
      paymentsTitle: "Metode pembayaran",
      paymentsHint:
        "Yang aktif di sini yang dilihat pelanggan saat checkout. Kunci yang dikosongkan tetap memakai kunci saat ini.",
      paymentsNeedOne: "Minimal satu metode pembayaran harus aktif.",
      paymentsSaved: "Metode pembayaran disimpan.",
      saveProfile: "Simpan profil",
      savePayments: "Simpan metode pembayaran",
      checkoutTitle: "Checkout pelanggan",
      checkoutOne: (label: string) => `Semua pembayaran memakai ${label}.`,
      checkoutMany: "Pelanggan memilih salah satu metode ini.",
      checkoutDummy: "Mode simulasi. Tidak ada uang yang ditarik.",
      unsaved: "Perubahan belum disimpan.",
      statusLive: "Aktif",
      statusOff: "Nonaktif",
      statusReady: "Kunci tersimpan",
      statusMissing: "Kunci belum lengkap",
      clientKeyHint: "Dikirim ke browser saat membuka jendela Midtrans.",
      secretKeep: "Kosongkan untuk mempertahankan kunci saat ini.",
      secretCurrent: "Kunci saat ini",
      midtransServer: "Server key",
      midtransClient: "Client key",
      midtransProduction: "Mode production",
      midtransProductionHint: "Snap production. Matikan untuk sandbox.",
      sumopodKey: "API key",
      sumopodWebhookSecret: "Webhook secret",
      sumopodBase: "Base URL API",
      sumopodMethod: "Jenis QRIS",
      sumopodSandbox: "Masih mengarah ke sandbox SumoPod.",
      duitkuTitle: "Duitku",
      duitkuHint: "VA bank, QRIS, e-wallet, minimarket, dan kartu. Pilih metode yang tampil di checkout.",
      duitkuMerchant: "Kode merchant",
      duitkuKey: "API key",
      duitkuProduction: "Mode produksi",
      duitkuProductionHint: "Matikan untuk sandbox Duitku (uji coba, tanpa uang sungguhan).",
      duitkuSandbox: "Masih mode sandbox: pembayaran tidak memakai uang sungguhan.",
      duitkuSync: "Ambil metode dari Duitku",
      duitkuSyncing: "Mengambil…",
      duitkuSynced: (n: number) => `${n} metode diambil dari Duitku. Simpan untuk menerapkan.`,
      duitkuMethods: "Metode pembayaran",
      duitkuMethodsEmpty: "Belum ada metode. Isi kode merchant dan API key, lalu ambil metode dari Duitku.",
      duitkuNeedMethod: "Aktifkan minimal satu metode Duitku.",
      duitkuNeedCreds: "Isi kode merchant dan API key Duitku.",
      duitkuFee: "Biaya",
      duitkuFeeNote: "Biaya hanya informasi dari Duitku; harga pesanan tidak berubah.",
      duitkuCallbackNote:
        "URL callback dikirim otomatis pada setiap transaksi. Tidak perlu diisi di dasbor Duitku.",
      duitkuAllOn: "Aktifkan semua",
      duitkuAllOff: "Matikan semua",
      webhookUrl: "URL webhook",
      copyWebhook: "Salin",
      copied: "URL disalin.",
      dummyHint: "Uji coba di aplikasi. Tidak ada uang yang ditarik.",
      dummyBlocked: "Simulasi dimatikan di production.",
      qrisHint: "Pelanggan membayar dengan QRIS di halaman SumoPod.",
      midtransHint: "QRIS, GoPay, virtual account, atau kartu lewat Snap.",
      manualTitle: "Transfer Bank (Manual)",
      manualHint:
        "Pelanggan transfer ke rekening Anda dan mengunggah bukti. Admin memverifikasi di menu Verifikasi Transfer.",
      manualAccounts: "Rekening tujuan",
      manualAccountsHint:
        "Tampil di halaman pembayaran pelanggan, lengkap dengan tombol salin. Maksimal 5 rekening.",
      manualAddAccount: "Tambah rekening",
      manualRemoveAccount: "Hapus rekening",
      manualBank: "Bank",
      manualBankPlaceholder: "Contoh: BCA",
      manualNumber: "Nomor rekening",
      manualNumberInvalid: "5–30 angka (boleh spasi atau tanda hubung).",
      manualHolder: "Atas nama",
      manualEmpty: "Belum ada rekening. Tambahkan minimal satu agar metode ini bisa diaktifkan.",
      manualInstructions: "Petunjuk untuk pelanggan",
      manualInstructionsHint: "Opsional. Contoh: jam verifikasi, atau kontak bila transfer dari luar negeri.",
      manualNeedAccount: "Lengkapi minimal satu rekening tujuan (bank, nomor, dan atas nama).",
      manualStatusMissing: "Rekening belum diisi",
      manualVerify: "Buka Verifikasi Transfer",
      recommendTitle: "Rekomendasi di checkout",
      recommendHint:
        "Metode ini dipilih otomatis, diberi label “Rekomendasi”, dan tampil paling atas. Catatan opsional tampil di atas pilihan metode.",
      recommendNone: "Tanpa rekomendasi",
      recommendMethod: "Metode yang direkomendasikan",
      recommendNote: "Catatan untuk pelanggan",
      recommendNotePlaceholder:
        "Contoh: Pembayaran otomatis segera hadir, untuk saat ini pembayaran dilakukan secara manual upload bukti transfer",
      recommendManualTip:
        "Payment gateway belum siap? Rekomendasikan Transfer Bank agar pelanggan langsung memilihnya.",
      recommendManualAction: "Rekomendasikan Transfer Bank",
      recommendManualNote:
        "Pembayaran otomatis segera hadir, untuk saat ini pembayaran dilakukan secara manual upload bukti transfer",
      recommendBadge: "Rekomendasi",
    },
    plansForm: {
      add: "Paket baru",
      edit: "Ubah paket",
      id: "ID",
      idHint: "Huruf kecil, angka, dan tanda hubung. Tidak bisa diubah setelah dibuat.",
      name: "Nama",
      description: "Deskripsi",
      price: "Harga per tahun (rupiah)",
      features: "Fitur",
      featuresHint: "Satu fitur per baris.",
      highlighted: "Disorot di halaman harga",
      active: "Dijual",
      sort: "Urutan",
      limits: "Batas",
      customDomain: "Domain sendiri",
      removeBranding: "Hapus merek BazeBuild",
      customDns: "Atur DNS domain (subdomain ke aplikasi lain)",
      pages: "Halaman per website",
      generations: "Generasi AI per bulan",
      rewrites: "Tulis ulang AI per bulan",
      storage: "Penyimpanan (MB)",
      analytics: "Riwayat analitik (hari)",
      blogPosts: "Artikel blog per website (0 = tanpa blog)",
      guestLinks: "Link undangan per nama tamu",
      guestbook: "Ucapan & doa",
      guestbookMax: "Ucapan maksimal",
      rsvp: "RSVP",
      event: "Website acara & undangan",
      eventHint:
        "Website undangan, ulang tahun, dan acara membayar sekali untuk masa aktif ini, tanpa perpanjangan. Kosongkan harga agar mereka membayar per tahun.",
      eventPrice: "Harga sekali bayar (rupiah)",
      eventMonths: "Masa aktif (bulan)",
      eventFeatures: "Fitur untuk website acara",
      eventFeaturesHint: "Satu fitur per baris. Kosongkan untuk memakai daftar fitur biasa.",
      invalidEvent: "Paket acara: harga angka nol atau lebih, masa aktif 1–24 bulan.",
      invalidId: "ID hanya huruf kecil, angka, dan tanda hubung.",
      invalidName: "Nama paket 1–40 karakter.",
      invalidPrice: "Harga harus angka nol atau lebih.",
    },
    tldForm: {
      edit: "Ubah harga",
      markup: "Markup (%)",
      fixed: "Markup tetap (rupiah)",
      round: "Pembulatan (rupiah)",
      enabled: "Dijual",
      registrar: "Harga dasar",
    },
    flagForm: {
      add: "Fitur baru",
      edit: "Ubah fitur",
      key: "Kunci",
      keyHint: "Huruf, angka, titik, atau tanda hubung. Tidak bisa diubah setelah dibuat.",
      description: "Deskripsi",
      rollout: "Persentase rilis",
      allowlist: "Daftar organisasi",
      allowlistHint: "Satu id organisasi per baris. Kosongkan agar berlaku untuk semua.",
    },
  },
} as const;

export type Copy = typeof t;
